import React, { useState, useRef, useEffect, useCallback } from 'react';
import { X, Play, Pause, Radio, RefreshCw } from 'lucide-react';
import { SecureAPI } from '../config.js';

const VOICE_CONFIG = {
  guide: '33045fd9-8010-43f6-b6b0-da3fbf326c29',
  therapist: '41745808-6790-4be4-83db-363e20db7e71',
  guest1: '5bb7de05-c8fe-426a-8fcc-ba4fc4ce9f9c',
  guest2: 'c01854384-4e4e-48d4-90d1-b22f760a58b5'
};

const INTRO_MUSIC_OPTIONS = [
  '/music/intro-outro-12.mp3',
  '/music/intro-outro-7.mp3',
  '/music/intro-outro-9.mp3',
  '/music/intro-outro-13.mp3'
];

const INTERMISSION_MUSIC_OPTIONS = [
  '/music/intro-outro-17.mp3',
  '/music/intro-outro-6.mp3',
  '/music/intro-outro-14.mp3',
  '/music/intro-outro-15.mp3',
  '/music/intro-outro-16.mp3'
];

const SelectedNewsStation = ({ 
  userName, 
  selectedSources, // ['cnn', 'techcrunch', 'abc']
  newsData, // Full news data object
  onClose 
}) => {
  const [ttsErrorCount, setTtsErrorCount] = useState(0);
const [cycleErrorCount, setCycleErrorCount] = useState(0);
const ttsErrorCountRef = useRef(0);
const cycleErrorCountRef = useRef(0);
const MAX_TTS_ERRORS = 3;
const MAX_CYCLE_ERRORS = 2;

  const [isNavigating, setIsNavigating] = useState(false);
const isNavigatingRef = useRef(false);
const [isGenerating, setIsGenerating] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [transcript, setTranscript] = useState([]);
  const [currentStatus, setCurrentStatus] = useState('');
  const [currentCardIndex, setCurrentCardIndex] = useState(0);
  const [newsCards, setNewsCards] = useState([]);
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const [currentCycle, setCurrentCycle] = useState(0);
  const [hasGuest, setHasGuest] = useState(false);
  const [guestName, setGuestName] = useState('');
  const [isLoadingCards, setIsLoadingCards] = useState(true);
  const [isSearchingComplete, setIsSearchingComplete] = useState(false);
  const [completedCards, setCompletedCards] = useState(new Set());
  const [heardNewsIds, setHeardNewsIds] = useState(new Set());
  const [showArticleConfirm, setShowArticleConfirm] = useState(false);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [showRefreshConfirm, setShowRefreshConfirm] = useState(false);
  const [scriptToCardMap, setScriptToCardMap] = useState({});
const currentLineIndexRef = useRef(0);

const audioQueueRef = useRef(new Map());      // Cache for preloaded audio
const isPreloadingRef = useRef(new Set());    // Track what's being preloaded
const abortControllerRef = useRef(new AbortController()); // Cancel pending requests
const lastNavigationTimeRef = useRef(0); // ✅ ADD THIS
const NAVIGATION_COOLDOWN = 10000; // ✅ ADD THIS (10 seconds)
  const shouldStopRef = useRef(false);
  const isPausedRef = useRef(false);
  const currentAudioRef = useRef(null);
  const intermissionAudioRef = useRef(null);
  const playbackSpeedRef = useRef(playbackSpeed);
  const scriptLinesRef = useRef([]);
  const transcriptEndRef = useRef(null);
  const newsCardsRef = useRef(null);
  const completedCardsRef = useRef(new Set());
  const heardNewsIdsRef = useRef(new Set());
  const availableArticlesRef = useRef([]); // Pool of RSS articles
  const googleFetchedRef = useRef(new Set()); // Track Google-fetched URLs
const activeAudioElements = useRef(new Set()); // ✅ Track ALL audio elements
const preloadAbortControllers = useRef(new Map()); // ✅ Separate controllers for preloads

// ============================================
// 1️⃣ SMART PRELOAD: Generate audio in background
// ============================================
// ============================================
const preloadAudio = async (text, voice) => {
  const cacheKey = `${voice}-${text}`;
  if (audioQueueRef.current.has(cacheKey)) return;

  // ✅ CREATE SEPARATE ABORT CONTROLLER FOR THIS PRELOAD
  const preloadController = new AbortController();
  preloadAbortControllers.current.set(cacheKey, preloadController);

  try {
    // ✅ USE SECURE API - NO MORE EXPOSED KEY
    const audioBlob = await SecureAPI.callHumeTTSSecure(
      text,
      VOICE_CONFIG[voice],
      1.1
    );

    const url = URL.createObjectURL(audioBlob);

    audioQueueRef.current.set(cacheKey, { url, text, voice, used: false });
    log(`✅ Preloaded: ${voice} - ${text.substring(0, 30)}...`);
  } catch (error) {
    if (error.name !== 'AbortError') {
      log(`⚠️ Preload error: ${error.message}`);
    }
  } finally {
    preloadAbortControllers.current.delete(cacheKey);
  }
};
const smartPreloadAudio = async (text, voice) => {
  if (shouldStopRef.current) return;

  const cacheKey = `${voice}-${text}`;
  const cached = audioQueueRef.current.get(cacheKey);

  if (cached && !cached.used) {
    // ✅ USE CACHED AUDIO
    cached.used = true;
    log(`⚡ Playing from cache: ${voice}`);

    return new Promise((resolve) => {
      if (shouldStopRef.current) {
        resolve();
        return;
      }
      
      const audio = new Audio(cached.url);
      
      if (currentAudioRef.current) {
        activeAudioElements.current.delete(currentAudioRef.current);
        currentAudioRef.current.pause();
      }
      
      activeAudioElements.current.add(audio);
      currentAudioRef.current = audio;
      
      audio.playbackRate = playbackSpeedRef.current;
      audio.volume = 1;

      // ✅ UPDATE TRANSCRIPT WHEN AUDIO STARTS PLAYING
      audio.onplay = () => {
        if (!shouldStopRef.current) {
          setTranscript(prev => {
            const lastEntry = prev[prev.length - 1];
            if (lastEntry && lastEntry.text === text && lastEntry.voice === voice) {
              return prev;
            }
            return [...prev, { voice, text, timestamp: Date.now() }];
          });
        }
      };

      audio.onended = () => {
        activeAudioElements.current.delete(audio);
        currentAudioRef.current = null;
        URL.revokeObjectURL(cached.url);
        audioQueueRef.current.delete(cacheKey);
        resolve();
      };

      audio.onerror = () => {
        activeAudioElements.current.delete(audio);
        currentAudioRef.current = null;
        resolve();
      };

      audio.play();
    });
  }

// ✅ GENERATE LIVE IF NOT CACHED
  try {
    if (shouldStopRef.current) return;
    
    // ✅ USE SECURE API - NO MORE EXPOSED KEY
    const audioBlob = await SecureAPI.callHumeTTSSecure(
      text,
      VOICE_CONFIG[voice],
      playbackSpeedRef.current
    );

    const trimmedBlob = await trimSilenceAggressive(audioBlob);
    const audioUrl = URL.createObjectURL(trimmedBlob);
    return new Promise((resolve) => {
      if (shouldStopRef.current) {
        URL.revokeObjectURL(audioUrl);
        resolve();
        return;
      }
      
      const audio = new Audio(audioUrl);
      
      if (currentAudioRef.current) {
        activeAudioElements.current.delete(currentAudioRef.current);
        currentAudioRef.current.pause();
      }
      
      activeAudioElements.current.add(audio);
      currentAudioRef.current = audio;
      
      audio.playbackRate = playbackSpeedRef.current;
      audio.volume = 1;

      // ✅ UPDATE TRANSCRIPT WHEN AUDIO STARTS PLAYING
      audio.onplay = () => {
        if (!shouldStopRef.current) {
          setTranscript(prev => {
            const lastEntry = prev[prev.length - 1];
            if (lastEntry && lastEntry.text === text && lastEntry.voice === voice) {
              return prev;
            }
            return [...prev, { voice, text, timestamp: Date.now() }];
          });
        }
      };

      audio.onended = () => {
        activeAudioElements.current.delete(audio);
        currentAudioRef.current = null;
        URL.revokeObjectURL(audioUrl);
        resolve();
      };

      audio.onerror = () => {
        activeAudioElements.current.delete(audio);
        currentAudioRef.current = null;
        URL.revokeObjectURL(audioUrl);
        resolve();
      };
      
      audio.play();
    });
  } catch (error) {
    log(`Audio error: ${error.message}`);
  }
};


// ============================================
// 2️⃣ INSTANT PLAYBACK FROM CACHE
// ============================================
const playInstantFromCache = async (text, host) => {
  const cacheKey = `${host}-${text}`;
  const cached = audioQueueRef.current.get(cacheKey);

  if (cached && !cached.used) {
    log(`⚡⚡⚡ INSTANT PLAYBACK FROM CACHE: ${host}`);
    
    cached.used = true;
    
    return new Promise((resolve) => {
      const audio = new Audio(cached.url);
      audio.playbackRate = playbackSpeedRef.current;
      
      // ✅ Stop previous audio
      if (currentAudioRef.current) {
        activeAudioElements.current.delete(currentAudioRef.current);
        currentAudioRef.current.pause();
        currentAudioRef.current = null;
      }
      
      // ✅ REGISTER IN GUEST LIST
      activeAudioElements.current.add(audio);
      currentAudioRef.current = audio;

      audio.onended = () => {
        URL.revokeObjectURL(cached.url);
        audioQueueRef.current.delete(cacheKey);
        activeAudioElements.current.delete(audio); // ✅ Remove from guest list
        currentAudioRef.current = null;
        resolve(true);
      };

      audio.onerror = () => {
        URL.revokeObjectURL(cached.url);
        audioQueueRef.current.delete(cacheKey);
        activeAudioElements.current.delete(audio); // ✅ Remove from guest list
        currentAudioRef.current = null;
        resolve(false);
      };

      audio.play().catch(err => {
        log(`Play error: ${err.message}`);
        activeAudioElements.current.delete(audio); // ✅ Remove from guest list
        resolve(false);
      });
    });
  }

  if (cached && cached.used) {
    log(`⚠️ Cache exists but already used: ${host}`);
  } else {
    log(`❌ NOT in cache yet: ${host}`);
  }
  
  return false;
};
useEffect(() => {
  if (transcriptEndRef.current) {
    transcriptEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }
}, [transcript]);

  const getLastUsedMusic = (type) => {
    try {
      const stored = localStorage.getItem(`lastUsed${type}Music`);
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  };
  const setLastUsedMusic = (type, url) => {
    try {
      localStorage.setItem(`lastUsed${type}Music`, JSON.stringify(url));
    } catch {
      log('⚠️ Could not save music preference');
    }
  };

const selectRandomMusicNeverRepeat = (musicOptions, type) => {
  const lastUsed = getLastUsedMusic(type);
  
  // Filter out the last used one
  const available = musicOptions.filter(url => url !== lastUsed);
  
  // If somehow we have no options, use all (shouldn't happen)
  const choices = available.length > 0 ? available : musicOptions;
  
  // Pick random from available
  const selected = choices[Math.floor(Math.random() * choices.length)];
  
  // Save for next time
  setLastUsedMusic(type, selected);
  
  log(`🎵 Selected ${type} music (avoiding repeat of ${lastUsed?.split('/').pop() || 'none'})`);
  
  return selected;
};
// ============================================
// 3️⃣ NUCLEAR CLEANUP - STOP ALL AUDIO INSTANTLY
// ============================================
const nuclearCleanupAudio = async () => {
  log(`🔴 NUCLEAR CLEANUP - Stopping ${activeAudioElements.current.size} audio elements`);
  
  // ✅ ABORT ALL PRELOAD REQUESTS (if using preloads)
  if (preloadAbortControllers.current) {
    preloadAbortControllers.current.forEach((controller, key) => {
      try {
        controller.abort();
        log(`🛑 Aborted preload: ${key}`);
      } catch (e) {}
    });
    preloadAbortControllers.current.clear();
  }
  
  // ✅ STOP ALL TRACKED AUDIO ELEMENTS
  activeAudioElements.current.forEach(audio => {
    try {
      audio.pause();
      audio.currentTime = 0;
      audio.volume = 0;
      audio.src = '';
      audio.load();
    } catch (e) {}
  });
  activeAudioElements.current.clear();
  
  // ✅ Clear main refs
  currentAudioRef.current = null;
  
  // ✅ Clear intermission ref
  if (intermissionAudioRef.current) {
    try {
      const audio = intermissionAudioRef.current;
      audio.pause();
      audio.currentTime = 0;
      audio.volume = 0;
      audio.src = '';
      audio.load();
      intermissionAudioRef.current = null;
    } catch (e) {
      intermissionAudioRef.current = null;
    }
  }
  
  // ✅ Clear audio queue and revoke URLs (if exists)
  if (audioQueueRef.current) {
    audioQueueRef.current.forEach(({ url }) => {
      try {
        URL.revokeObjectURL(url);
      } catch (e) {}
    });
    audioQueueRef.current.clear();
  }
  
  // ✅ Wait for complete cleanup
  await new Promise(resolve => setTimeout(resolve, 1000));
  
  log(`✅ Nuclear cleanup complete - ${activeAudioElements.current.size} audio elements remaining`);
};


// ============================================
// 4️⃣ UPDATED handleNextCard WITH CLEANUP
// ============================================
const handleNextCard = async () => {
  const now = Date.now();
  if (now - lastNavigationTimeRef.current < NAVIGATION_COOLDOWN) {
    const remaining = Math.ceil((NAVIGATION_COOLDOWN - (now - lastNavigationTimeRef.current)) / 1000);
    log(`⏳ Please wait ${remaining} seconds before navigating`);
    return;
  }

  if (isNavigatingRef.current) {
    log('⚠️ Navigation already in progress');
    return;
  }

  const nextIndex = currentCardIndex + 1;
  if (nextIndex >= newsCards.length || !isPlaying) return;

  lastNavigationTimeRef.current = now;
  isNavigatingRef.current = true;
  setIsNavigating(true);

  log(`⏭️ Skipping to next card ${currentCardIndex} → ${nextIndex}`);

  shouldStopRef.current = true;
  isPausedRef.current = true;

  // ✅ SHOW SHIMMER IMMEDIATELY
  setTranscript([{ voice: 'system', text: 'shimmer', timestamp: Date.now() }]);

  const currentCard = newsCards[currentCardIndex];
  if (currentCard) {
    completedCardsRef.current.add(currentCard.id);
    setCompletedCards(new Set(completedCardsRef.current));
  }

  await nuclearCleanupAudio();
  await new Promise(resolve => setTimeout(resolve, 300));

  setCurrentCardIndex(nextIndex);
  setNewsCards(prev => prev.map((card, idx) => ({
    ...card,
    isActive: idx === nextIndex,
    isReady: idx <= nextIndex
  })));

  if (newsCardsRef.current?.children[nextIndex]) {
    newsCardsRef.current.children[nextIndex].scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'center'
    });
  }

  shouldStopRef.current = false;
  isPausedRef.current = false;

  // ✅ GENERATE TRANSITION + PRELOAD NEXT SPEAKERS IN PARALLEL
  log('🚀 PARALLEL: Generating transition + Preloading speakers...');

  const fromCard = newsCards[currentCardIndex];
  const nextCard = newsCards[nextIndex];
  const transitionPromise = generateCardTransition(fromCard, nextCard, true);

  const linesPerNews = Math.floor(scriptLinesRef.current.length / newsCards.length);
  const startLine = nextIndex * linesPerNews;
  const lines = scriptLinesRef.current;

  const preloadPromises = [];
  let preloadCount = 0;

  for (let i = startLine; i < Math.min(startLine + 20, lines.length) && preloadCount < 3; i++) {
    const line = lines[i];
    const guideMatch = line.match(/Guide:\s*(.+)/i);
    const companionMatch = line.match(/Companion:\s*(.+)/i);
    const guestMatch = hasGuest && guestName
      ? line.match(new RegExp(`${guestName}:\\s*(.+)`, 'i'))
      : null;

    if (guideMatch) {
      const text = guideMatch[1].trim();
      log(`📥 PRIORITY preload [${preloadCount}]: Guide`);
      preloadPromises.push(preloadAudio(text, 'guide'));
      preloadCount++;
    } else if (companionMatch) {
      const text = companionMatch[1].trim();
      log(`📥 PRIORITY preload [${preloadCount}]: Therapist`);
      preloadPromises.push(preloadAudio(text, 'therapist'));
      preloadCount++;
    } else if (guestMatch) {
      const text = guestMatch[1].trim();
      const guestVoice = guestName === 'Kenny' ? 'guest1' : 'guest2';
      log(`📥 PRIORITY preload [${preloadCount}]: ${guestName}`);
      preloadPromises.push(preloadAudio(text, guestVoice));
      preloadCount++;
    }
  }

  const transitionText = await transitionPromise;
  log(`✅ Transition generated`);

  // ✅ CLEAR SHIMMER BEFORE PLAYING TRANSITION
  setTranscript([]);

  await playAudio(transitionText, Math.random() < 0.5 ? 'guide' : 'therapist');

  if (shouldStopRef.current) {
    isNavigatingRef.current = false;
    setIsNavigating(false);
    return;
  }

  // ✅ WAIT FOR PRELOADS TO FINISH
  await Promise.all(preloadPromises);
  await new Promise(resolve => setTimeout(resolve, 200));

  if (activeAudioElements.current.size <= 1 && !shouldStopRef.current) {
    log(`✅ Safe to resume at line ${startLine}`);
    isNavigatingRef.current = false;
    setIsNavigating(false);
    await resumeFromLine(startLine);
  } else {
    log(`⚠️ Not safe to resume - ${activeAudioElements.current.size} audio elements`);
    isNavigatingRef.current = false;
    setIsNavigating(false);
  }
};
// ============================================
// 5️⃣ UPDATED handlePrevCard WITH CLEANUP
// ============================================
const handlePrevCard = async () => {
  const now = Date.now();
  if (now - lastNavigationTimeRef.current < NAVIGATION_COOLDOWN) {
    const remaining = Math.ceil((NAVIGATION_COOLDOWN - (now - lastNavigationTimeRef.current)) / 1000);
    log(`⏳ Please wait ${remaining} seconds before navigating`);
    return;
  }

  if (isNavigatingRef.current) {
    log('⚠️ Navigation already in progress');
    return;
  }

  const prevIndex = currentCardIndex - 1;
  if (prevIndex < 0 || !isPlaying) return;

  lastNavigationTimeRef.current = now;
  isNavigatingRef.current = true;
  setIsNavigating(true);

  log(`⏮️ Going back to card ${currentCardIndex} → ${prevIndex}`);

  shouldStopRef.current = true;
  isPausedRef.current = true;

  // ✅ SHOW SHIMMER IMMEDIATELY
  setTranscript([{ voice: 'system', text: 'shimmer', timestamp: Date.now() }]);

  await nuclearCleanupAudio();
  await new Promise(resolve => setTimeout(resolve, 300));

  setCurrentCardIndex(prevIndex);
  setNewsCards(prev => prev.map((card, idx) => ({
    ...card,
    isActive: idx === prevIndex,
    isReady: idx <= prevIndex
  })));

  if (newsCardsRef.current?.children[prevIndex]) {
    newsCardsRef.current.children[prevIndex].scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'center'
    });
  }

  shouldStopRef.current = false;
  isPausedRef.current = false;

  // ✅ GENERATE TRANSITION + PRELOAD PREV SPEAKERS IN PARALLEL
  log('🚀 PARALLEL: Generating transition + Preloading speakers...');

  const fromCard = newsCards[currentCardIndex];
  const prevCard = newsCards[prevIndex];
  const transitionPromise = generateCardTransition(fromCard, prevCard, false);

  const linesPerNews = Math.floor(scriptLinesRef.current.length / newsCards.length);
  const startLine = prevIndex * linesPerNews;
  const lines = scriptLinesRef.current;

  const preloadPromises = [];
  let preloadCount = 0;

  for (let i = startLine; i < Math.min(startLine + 20, lines.length) && preloadCount < 3; i++) {
    const line = lines[i];
    const guideMatch = line.match(/Guide:\s*(.+)/i);
    const companionMatch = line.match(/Companion:\s*(.+)/i);
    const guestMatch = hasGuest && guestName
      ? line.match(new RegExp(`${guestName}:\\s*(.+)`, 'i'))
      : null;

    if (guideMatch) {
      const text = guideMatch[1].trim();
      log(`📥 PRIORITY preload [${preloadCount}]: Guide`);
      preloadPromises.push(preloadAudio(text, 'guide'));
      preloadCount++;
    } else if (companionMatch) {
      const text = companionMatch[1].trim();
      log(`📥 PRIORITY preload [${preloadCount}]: Therapist`);
      preloadPromises.push(preloadAudio(text, 'therapist'));
      preloadCount++;
    } else if (guestMatch) {
      const text = guestMatch[1].trim();
      const guestVoice = guestName === 'Kenny' ? 'guest1' : 'guest2';
      log(`📥 PRIORITY preload [${preloadCount}]: ${guestName}`);
      preloadPromises.push(preloadAudio(text, guestVoice));
      preloadCount++;
    }
  }

  const transitionText = await transitionPromise;
  log(`✅ Transition generated`);

  // ✅ CLEAR SHIMMER BEFORE PLAYING TRANSITION
  setTranscript([]);

  await playAudio(transitionText, Math.random() < 0.5 ? 'guide' : 'therapist');

  if (shouldStopRef.current) {
    isNavigatingRef.current = false;
    setIsNavigating(false);
    return;
  }

  // ✅ WAIT FOR PRELOADS TO FINISH
  await Promise.all(preloadPromises);
  await new Promise(resolve => setTimeout(resolve, 200));

  if (activeAudioElements.current.size <= 1 && !shouldStopRef.current) {
    log(`✅ Safe to resume at line ${startLine}`);
    isNavigatingRef.current = false;
    setIsNavigating(false);
    await resumeFromLine(startLine);
  } else {
    log(`⚠️ Not safe to resume - ${activeAudioElements.current.size} audio elements`);
    isNavigatingRef.current = false;
    setIsNavigating(false);
  }
};
// ============================================
// 6️⃣ UPDATED confirmRefresh WITH CLEANUP
// ============================================
const confirmRefresh = async () => {
  setShowRefreshConfirm(false);
  
  if (!isPlaying) return;
  
  shouldStopRef.current = true;
  isPausedRef.current = true;
  
  // ✅ NUCLEAR CLEANUP - kills all audio
  await nuclearCleanupAudio();
  await new Promise(resolve => setTimeout(resolve, 600));
  
  const transitionPhrases = [
    `Alright ${userName}, let's find some fresh stories.`,
    `${userName}, searching for the latest updates.`,
    `Okay ${userName}, grabbing new news for you.`,
  ];
  
  const randomPhrase = transitionPhrases[Math.floor(Math.random() * transitionPhrases.length)];
  
  shouldStopRef.current = false;
  isPausedRef.current = false;
  
  await playAudio(randomPhrase, Math.random() < 0.5 ? 'guide' : 'therapist');
  
  setTranscript([{
    voice: 'system',
    text: 'Searching for fresh news...',
    timestamp: Date.now(),
    isSearching: true
  }]);
  
  setCurrentStatus('Gathering news...');
  setIsLoadingCards(true);
  
  completedCardsRef.current.clear();
  setCompletedCards(new Set());
  setCurrentCardIndex(0);
  
  await runNewsCycle();
};

// ============================================
// 7️⃣ CLEANUP ON UNMOUNT
// ============================================
useEffect(() => {
  return () => {
    shouldStopRef.current = true;
    isPausedRef.current = true;
    
    try {
      abortControllerRef.current.abort();
    } catch (e) {}
    
    if (currentAudioRef.current) {
      currentAudioRef.current.pause();
      currentAudioRef.current = null;
    }
    
    if (intermissionAudioRef.current) {
      intermissionAudioRef.current.pause();
      intermissionAudioRef.current = null;
    }
    
    audioQueueRef.current.forEach(({ url }) => {
      try {
        URL.revokeObjectURL(url);
      } catch (e) {}
    });
    audioQueueRef.current.clear();
    isPreloadingRef.current.clear();
  };
}, []);
const resumeFromLine = async (startLineIndex) => {
  log(`▶️ Resuming from line ${startLineIndex}`);
  const lines = scriptLinesRef.current;
  
  if (!lines || lines.length === 0) {
    log('⚠️ No script lines available');
    setTranscript([]); // Clear shimmer if no lines
    return;
  }
  
  // ✅ DETECT WHICH CARD THIS LINE BELONGS TO
  let targetCardIndex = 0;
  for (let cardIdx = 0; cardIdx < newsCards.length; cardIdx++) {
    const range = scriptToCardMap[cardIdx];
    if (range && startLineIndex >= range.start && startLineIndex < range.end) {
      targetCardIndex = cardIdx;
      break;
    }
  }
  
  log(`🎯 Resuming at Card ${targetCardIndex} (line ${startLineIndex})`);
  
  // ✅ INSTANT CARD SYNC
  setCurrentCardIndex(targetCardIndex);
  setNewsCards(prev => prev.map((card, idx) => ({
    ...card,
    isActive: idx === targetCardIndex,
    isReady: idx <= targetCardIndex
  })));
  
  // ✅ INSTANT SCROLL
  if (newsCardsRef.current?.children[targetCardIndex]) {
    newsCardsRef.current.children[targetCardIndex].scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'center'
    });
  }
  
  currentLineIndexRef.current = startLineIndex;
  
  // ✅ CLEAR SHIMMER RIGHT BEFORE STARTING PLAYBACK
  log('✅ Resume playback complete - clearing shimmer, showing transcript');
  setTranscript([]);
  
  for (let i = startLineIndex; i < lines.length; i++) {
    currentLineIndexRef.current = i;
    
    if (shouldStopRef.current) {
      log('⏹️ Stop requested, breaking resume loop');
      break;
    }
    
    while (isPausedRef.current && !shouldStopRef.current) {
      await new Promise(resolve => setTimeout(resolve, 50));
    }
    
    if (shouldStopRef.current) break;
    
    // ✅ CONTINUOUS CARD TRACKING (check if we moved to next card)
    let currentCardIndex = targetCardIndex;
    for (let cardIdx = 0; cardIdx < newsCards.length; cardIdx++) {
      const range = scriptToCardMap[cardIdx];
      if (range && i >= range.start && i < range.end) {
        currentCardIndex = cardIdx;
        break;
      }
    }
    
    // ✅ UPDATE CARD IF CHANGED
    if (currentCardIndex !== targetCardIndex) {
      targetCardIndex = currentCardIndex;
      
      setCurrentCardIndex(currentCardIndex);
      setNewsCards(prev => prev.map((card, idx) => ({
        ...card,
        isActive: idx === currentCardIndex,
        isReady: idx <= currentCardIndex
      })));
      
      if (newsCardsRef.current?.children[currentCardIndex]) {
        newsCardsRef.current.children[currentCardIndex].scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'center'
        });
      }
      
      log(`✅ Card updated during resume: ${currentCardIndex}`);
    }
    
    const line = lines[i];
    const guideMatch = line.match(/Guide:\s*(.+)/i);
    const companionMatch = line.match(/Companion:\s*(.+)/i);
    const guestMatch = hasGuest && guestName
      ? line.match(new RegExp(`${guestName}:\\s*(.+)`, 'i'))
      : null;

    if (guideMatch || companionMatch || guestMatch) {
      let voice, text;
      
      if (guideMatch) {
        voice = 'guide';
        text = guideMatch[1].trim();
      } else if (companionMatch) {
        voice = 'therapist';
        text = companionMatch[1].trim();
      } else if (guestMatch) {
        voice = guestName === 'Kenny' ? 'guest1' : 'guest2';
        text = guestMatch[1].trim();
      }

      await playAudio(text, voice);
      
      if (shouldStopRef.current) break;
    }
  }
  
  log('✅ Resume playback complete');
};



  const log = useCallback((message) => {
    console.log(`[Selected News Station] ${message}`);
  }, []);

  // ✅ Fetch news from RSS or Google
const fetchNewsArticles = async () => {
  try {
    log('📡 Fetching news from selected sources...');
    
    // Step 1: Get all available RSS articles from selected sources
    const rssArticles = [];
    
    for (const sourceId of selectedSources) {
      const sourceData = newsData[sourceId];
      if (sourceData && sourceData.stories) {
        sourceData.stories.forEach((story, idx) => {
          const articleId = `${sourceId}-${idx}-${story.url}`;
          
          // Skip if already heard
          if (!heardNewsIdsRef.current.has(articleId)) {
            rssArticles.push({
              id: articleId,
              title: story.title,
              snippet: story.description || story.title,
              source: sourceData.name,
              url: story.source,
              time: story.time || 'Recently',
              imageUrl: story.image,
              sourceId: sourceId
            });
          }
        });
      }
    }
    
    log(`📊 RSS Pool: ${rssArticles.length} unheard articles`);
    
    // Step 2: If we have enough RSS articles, use them
    if (rssArticles.length >= 5) {
      const shuffled = rssArticles.sort(() => Math.random() - 0.5);
      const selected = shuffled.slice(0, 5);
      
      // Mark as heard
      selected.forEach(article => {
        heardNewsIdsRef.current.add(article.id);
      });
      setHeardNewsIds(new Set(heardNewsIdsRef.current));
      
      log(`✅ Using ${selected.length} RSS articles`);
      return selected;
    }
    
    // Step 3: Need to fetch from Yahoo Search
    log('🔍 RSS pool exhausted, fetching from Yahoo Search...');
    
    const yahooArticles = [];
    
    for (const sourceId of selectedSources) {
      try {
        // ✅ NEW: Use Yahoo Search instead of Google
        const searchUrl = `https://api.hume.ai/v0/universalWebSearch?query=${encodeURIComponent(sourceId)}&maxResults=5`;
        
        log(`🔗 Yahoo Search URL: ${searchUrl}`);
        console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
        console.log(`📍 Fetching Yahoo results for: ${sourceId.toUpperCase()}`);
        console.log(`🔗 Full URL: ${searchUrl}`);
        console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
        
        const response = await fetch(searchUrl, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json'
          }
        });
        
        console.log(`✅ Yahoo response status: ${response.status}`);
        
        if (response.ok) {
          const data = await response.json();
          
          console.log(`📊 Yahoo returned ${data.results?.length || 0} results for ${sourceId}`);
          
          if (data.results && data.results.length > 0) {
            data.results.forEach((result, idx) => {
              const articleId = `yahoo-${sourceId}-${result.url}`;
              
              console.log(`\n  Result #${idx + 1}:`);
              console.log(`    Title: ${result.title}`);
              console.log(`    URL: ${result.url}`);
              console.log(`    Source: ${result.source}`);
              
              if (!heardNewsIdsRef.current.has(articleId)) {
                yahooArticles.push({
                  id: articleId,
                  title: result.title,
                  snippet: result.snippet,
                  source: sourceId.toUpperCase(),
                  url: result.url,
                  time: 'Latest',
                  imageUrl: result.image || null,
                  sourceId: sourceId
                });
                console.log(`    ✅ Added to collection`);
              } else {
                console.log(`    ⚠️ Already heard this article`);
              }
            });
          }
        } else {
          console.log(`❌ Yahoo Search failed with status: ${response.status}`);
          const errorData = await response.json().catch(() => ({}));
          console.log(`Error details:`, errorData);
        }
      } catch (error) {
        log(`⚠️ Yahoo Search error for ${sourceId}: ${error.message}`);
        console.error(`❌ Fetch error for ${sourceId}:`, error);
        console.error(`Stack trace:`, error.stack);
      }
    }
    
    log(`📊 Yahoo fetched: ${yahooArticles.length} new articles`);
    
    // Step 4: Combine RSS + Yahoo
    const combined = [...rssArticles, ...yahooArticles];
    
    if (combined.length === 0) {
      log('❌ No articles available - waiting 30 seconds...');
      await new Promise(resolve => setTimeout(resolve, 30000));
      return fetchNewsArticles(); // Retry
    }
    
    // Shuffle and take 5
    const shuffled = combined.sort(() => Math.random() - 0.5);
    const selected = shuffled.slice(0, 5);
    
    // Mark as heard
    selected.forEach(article => {
      heardNewsIdsRef.current.add(article.id);
    });
    setHeardNewsIds(new Set(heardNewsIdsRef.current));
    
    log(`✅ Selected ${selected.length} articles (RSS: ${rssArticles.length}, Yahoo: ${yahooArticles.length})`);
    return selected;
    
  } catch (error) {
    log(`❌ Fetch error: ${error.message}`);
    throw error;
  }
};

  // ✅ Generate script
const generateStationScript = async (newsItems, includeGuest = false, guestName = '', isResuming = false) => {
  const timeOfDay = getTimeOfDay();
  
  const newsContext = newsItems.map((item, idx) => 
    `${idx + 1}. ${item.title}\nSource: ${item.source}\nSummary: ${item.snippet}`
  ).join('\n\n');

  const guestInfo = includeGuest ? `

**GUEST FEATURE:** ${guestName} (interesting guest)
- NOT a journalist or expert - just an engaged listener
- Joins naturally around exchange 8-12 (not at start)
- Brings fresh perspective, curiosity, or personal interest
- Can ask questions, react, share observations
- Makes it feel like 3 friends discussing news
` : '';
const prompt = `Create a live news broadcast covering these stories naturally.

**LISTENER:** ${userName}
**TIME OF DAY:** ${timeOfDay}
**LATEST NEWS:**
${newsContext}
${guestInfo}

**CRITICAL: JUMP STRAIGHT INTO STORIES - NO RE-GREETING**

❌ NEVER use exclamation marks (!)
❌ Don't be flat or monotone
❌ Avoid robotic delivery 

✅ **START IMMEDIATELY WITH NEWS (NO INTRO):**
Guide: "So looking into the news, there's this story about [topic]..."
Companion: "Yeah, and apparently [detail]..."

Guide: "Interesting development—[event] happened..."
Companion: "Right, so [context]..."

Guide: "Alright, so first thing—[topic] is [event]..."
Companion: "Yeah, [source] is reporting that [detail]..."

❌ **NEVER START WITH:**
- "Let's dive into today's news..."
- "Welcome to your selected sources..."
- "Good evening ${userName}..."

**HOSTS:**
- Guide: Main host
- Companion: Co-host
${includeGuest ? `- ${guestName}: Guest` : ''}

**CRITICAL - NO PERSONAL OPINIONS OR "I" STATEMENTS:**
❌ NEVER say: "I think", "I believe", "I feel", "I noticed", "I saw"
❌ NEVER say: "In my opinion", "From my perspective", "I find this"
✅ INSTEAD say: "This shows", "The data indicates", "Reports suggest", "Analysts note"

**FOCUS ON THE NEWS, NOT OPINIONS:**
✅ "The company announced major layoffs today"
✅ "Markets responded with a five percent drop"
✅ "Experts are calling this a turning point"
✅ "The implications here are significant"
✅ "Sources indicate this could expand further"

❌ "I think this is bad news"
❌ "I'm concerned about this development"
❌ "I believe this will hurt them"
❌ "I noticed the CEO seemed nervous"

**USING "WE" - ONLY FOR SHOW OPERATIONS:**
✅ "We're covering..." (the show is covering)
✅ "We found that..." (collective reporting)
✅ "Let's look at..." (directing attention)

❌ "We think this matters" (personal opinion)
❌ "We're excited about this" (personal feeling)

**NATURAL CONVERSATION FLOW - CRITICAL:**

**VARIETY IN DELIVERY (Mix these styles randomly):**
**CONVERSATION STYLE - NATURAL BUT PROFESSIONAL:**
- Spell out ALL numbers as words
- Natural reactions: "Oh wow", "Interesting", "Wait, so", "Hmm", "Actually", "You know what's wild?"
- Use conversational fillers: "I mean", "Like", "Right?", "You see", "The thing is"
- Each turn: 15-45 words
- Sound like two informed people chatting about news they find interesting
- Show genuine curiosity and engagement with the stories
- React to information naturally: "That's significant", "Hold on, that's huge", "Okay so get this"
- Let the story unfold through natural conversation
- Focus on FACTS but delivered conversationally

**SPEAKING NATURALLY:**
✅ "So get this - the company just announced ten million in funding"
✅ "Wait, that's actually a pretty big deal"
✅ "Okay so here's what's interesting about this"
✅ "I mean, when you look at the numbers, it's significant"
✅ "Right, so the stock jumped fifteen percent - that's notable"

❌ "The company announced ten million dollars in funding" (too formal/robotic)
❌ "This development is noteworthy" (sounds like reading)
❌ "According to reports" (too stiff)

**Style 1: Direct with filler in between**
Guide: "CNN just reported the train strike is expanding. Uhm, that's going to affect millions."
Companion: "Mhm."
Guide: "Yeah so commuters in five major cities are now impacted."

**Style 2: Start clean, react naturally**
Companion: "NASA announced a new Mars mission launching next month."
Guide: "Wait, that's actually really cool."
Companion: "Right? So this one is focused on finding water sources."

**Style 3: Conversational build-up**
Guide: "Okay so get this - the tech giant just got fined two billion dollars."
Companion: "Oh wow, for what?"
Guide: "Antitrust violations. Yeah, regulators said they abused market dominance."

**Style 4: Background acknowledgments**
Guide: "Reuters is reporting major flooding in the Midwest."
Companion: "Hmm."
Guide: "So emergency services are evacuating thousands of residents right now."

**CONVERSATIONAL ELEMENTS:**
- Use "uhm", "uh", "hmm" naturally (not every sentence!)
- Background responses: "Mhm", "Hmm", "Yeah", "Right", "Oh", "Wow"
- Natural reactions AFTER information: "That's significant", "That's rough", "Interesting"
- Sentence starters: "So", "Okay so", "Wait", "Actually", "Get this"
- Mid-sentence: "I mean", "you know", "like"
- Follow-ups: "Right?", "You see?", "Make sense?"

**PACING & RHYTHM:**
- NOT every turn needs a filler word
- Mix short reactive responses with longer explanations
- One host can say just "Mhm" or "Yeah" to acknowledge while other continues
- Vary between quick back-and-forth and longer statements
- Sometimes start direct: "The president announced" (no filler needed)
- Sometimes start casual: "So uhm, this announcement" (natural hesitation)
✅ Say "you" when relevant:
- "This could affect your commute"
- "If you're heading downtown..."
- "You'll want to avoid..."
- "This is important for you to know"

❌ NEVER say:
- "for everyone"
- "for all of us"
- "for people"
- "this affects everyone"

Instead say:
- "for you"
- "this affects you"
- "if you're in the area"

**EXAMPLES OF GOOD FLOW:**

Guide: "Apple's new iPhone just launched with major upgrades."
Companion: "Mhm."
Guide: "Yeah, so pre-orders are already breaking records."

Companion: "Okay so there's this wildfire spreading in California."
Guide: "Wait, how bad is it?"
Companion: "Pretty serious. Uhm, over ten thousand acres burned so far."

Guide: "The Fed just announced they're keeping interest rates steady."
Companion: "Hmm, yeah that makes sense given inflation."
Guide: "Right, so markets are responding positively to this."

**CONVERSATION STYLE:**
- Spell out ALL numbers as words
- Natural transitions: "Yeah", "Right", "Hold on", "Hmm"
- Each turn: 15-45 words
- Sound like analysts briefing ${userName}
- Let the story unfold naturally without announcing sources
- Focus on FACTS and DEVELOPMENTS
- MIX conversational styles - variety is key
- Do not start with "so" every sentence
- Use fillers naturally, not robotically

**STORY FLOW:**
1. Jump straight into the first story immediately
2. Discuss the story factually
3. Move to next story with smooth transition
4. NO SOURCE ANNOUNCEMENTS between stories

**PACING:**
- 2-3 exchanges per simple story
- 3-5 exchanges per standard story
- 4-6 exchanges per complex story
- Keep momentum

**TRANSITION TO NEXT CYCLE:**
- After covering news, say "Let's switch gears while we gather more updates"
- Keep it open-ended and continuous

Output ONLY the script:
Guide: [text]
Companion: [text]
${includeGuest ? `${guestName}: [text]` : ''}

CRITICAL:
- NO "I think", "I believe", "I feel" statements
- NO greetings or re-introductions
- Jump STRAIGHT into first story
- NO source announcements
- Focus on news facts, not personal reactions
- MIX delivery styles - not every line needs "uhm"
- Use background acknowledgments naturally`;
  try {
    // ✅ CHANGED: Use SecureAPI instead of direct Azure call
    const response = await SecureAPI.callAzureOpenAI(
      [
        { 
          role: 'system', 
          content: 'You are a professional broadcast script writer. Write natural, conversational news scripts that jump straight into stories without greetings or source announcements.'
        },
        { role: 'user', content: prompt }
      ],
      {
        temperature: 0.85,
        max_tokens: 1500
      }
    );

    return response.choices[0].message.content;
  } catch (error) {
    log(`❌ Script error: ${error.message}`);
    throw error;
  }
};


  // ✅ Audio playback (same as LocalNewsStation)
const trimSilenceAggressive = async (blob) => {
  try {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    const arrayBuffer = await blob.arrayBuffer();
    const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
    
    // ✅ AGGRESSIVE: Very low threshold to catch any silence
    const threshold = 0.005; // 0.5% volume (very sensitive)
    const sampleRate = audioBuffer.sampleRate;
    const channels = audioBuffer.numberOfChannels;
    
    // Find first non-silent sample
    let start = 0;
    const channel0 = audioBuffer.getChannelData(0);
    
    // ✅ Scan for first sound (no padding on start)
    for (let i = 0; i < channel0.length; i++) {
      if (Math.abs(channel0[i]) > threshold) {
        start = i; // ✅ No padding - cut right at sound start
        break;
      }
    }
    
    // Find last non-silent sample
    let end = channel0.length;
    
    // ✅ Scan backwards for last sound (no padding on end)
    for (let i = channel0.length - 1; i >= start; i--) {
      if (Math.abs(channel0[i]) > threshold) {
        end = i + 1; // ✅ No padding - cut right after sound ends
        break;
      }
    }
    
    // Create trimmed buffer
    const trimmedLength = end - start;
    if (trimmedLength <= 0) return blob;
    
    const trimmedBuffer = audioContext.createBuffer(channels, trimmedLength, sampleRate);
    
    for (let ch = 0; ch < channels; ch++) {
      const sourceData = audioBuffer.getChannelData(ch);
      const targetData = trimmedBuffer.getChannelData(ch);
      targetData.set(sourceData.slice(start, end));
    }
    
    const originalDuration = (audioBuffer.length / sampleRate).toFixed(3);
    const trimmedDuration = (trimmedLength / sampleRate).toFixed(3);
    const saved = (originalDuration - trimmedDuration).toFixed(3);
    
    if (saved > 0.05) {
      log(`✂️ Aggressive trim: ${saved}s removed (${originalDuration}s → ${trimmedDuration}s)`);
    }
    
    const trimmedBlob = audioBufferToWavBlob(trimmedBuffer);
    await audioContext.close();
    
    return trimmedBlob;
  } catch (error) {
    log(`⚠️ Aggressive trim failed: ${error.message}`);
    return blob;
  }
};
  const audioBufferToWavBlob = (audioBuffer) => {
    const numberOfChannels = audioBuffer.numberOfChannels;
    const sampleRate = audioBuffer.sampleRate;
    const length = audioBuffer.length;
    
    const interleaved = new Float32Array(length * numberOfChannels);
    for (let i = 0; i < length; i++) {
      for (let channel = 0; channel < numberOfChannels; channel++) {
        interleaved[i * numberOfChannels + channel] = audioBuffer.getChannelData(channel)[i];
      }
    }
    
    const pcmData = new Int16Array(interleaved.length);
    for (let i = 0; i < interleaved.length; i++) {
      const sample = Math.max(-1, Math.min(1, interleaved[i]));
      pcmData[i] = sample < 0 ? sample * 0x8000 : sample * 0x7FFF;
    }
    
    const wavBuffer = new ArrayBuffer(44 + pcmData.length * 2);
    const view = new DataView(wavBuffer);
    
    const writeString = (offset, string) => {
      for (let i = 0; i < string.length; i++) {
        view.setUint8(offset + i, string.charCodeAt(i));
      }
    };
    
    writeString(0, 'RIFF');
    view.setUint32(4, 36 + pcmData.length * 2, true);
    writeString(8, 'WAVE');
    writeString(12, 'fmt ');
    view.setUint32(16, 16, true);
    view.setUint16(20, 1, true);
    view.setUint16(22, numberOfChannels, true);
    view.setUint32(24, sampleRate, true);
    view.setUint32(28, sampleRate * numberOfChannels * 2, true);
    view.setUint16(32, numberOfChannels * 2, true);
    view.setUint16(34, 16, true);
    writeString(36, 'data');
    view.setUint32(40, pcmData.length * 2, true);
    
    const dataView = new Int16Array(wavBuffer, 44);
    dataView.set(pcmData);
    
    return new Blob([wavBuffer], { type: 'audio/wav' });
  };


// ===========================================
// 4. INSTANT PAUSE - STOP ALL AUDIO IMMEDIATELY
// ===========================================
const togglePlayPause = async () => {
  if (isPaused) {
    // RESUME
    log('▶️ Resuming playback...');
    
    setIsPaused(false);
    isPausedRef.current = false;
    shouldStopRef.current = false;
    setIsPlaying(true);
    
    // ✅ SHOW SHIMMER IMMEDIATELY
    setTranscript([{ voice: 'system', text: 'shimmer', timestamp: Date.now() }]);
    
    if (scriptLinesRef.current && scriptLinesRef.current.length > 0) {
      const currentLineIndex = currentLineIndexRef.current || 0;
      
      log(`▶️ Resuming from line ${currentLineIndex}...`);
      
      // ✅ KEEP SHIMMER VISIBLE - it will be cleared when resumeFromLine starts playing
      await resumeFromLine(currentLineIndex);
    }
    
  } else if (isPlaying) {
    // PAUSE - INSTANT STOP (WITHOUT CLEARING TRANSCRIPT)
    log('⏸️ INSTANT PAUSE - Keeping transcript visible');
    
    // ✅ Set pause flags FIRST
    setIsPaused(true);
    isPausedRef.current = true;
    shouldStopRef.current = true;
    
    // ✅ STOP AUDIO WITHOUT CLEARING TRANSCRIPT
    log(`🔴 Stopping ${activeAudioElements.current.size} audio elements (keeping transcript)`);
    
    // Stop all audio
    activeAudioElements.current.forEach(audio => {
      try {
        audio.pause();
        audio.currentTime = 0;
        audio.volume = 0;
        audio.src = '';
        audio.load();
      } catch (e) {}
    });
    activeAudioElements.current.clear();
    
    // Clear main refs
    currentAudioRef.current = null;
    
    // Clear intermission ref
    if (intermissionAudioRef.current) {
      try {
        const audio = intermissionAudioRef.current;
        audio.pause();
        audio.currentTime = 0;
        audio.volume = 0;
        audio.src = '';
        audio.load();
        intermissionAudioRef.current = null;
      } catch (e) {
        intermissionAudioRef.current = null;
      }
    }
    
    // Clear audio queue
    if (audioQueueRef.current) {
      audioQueueRef.current.forEach(({ url }) => {
        try {
          URL.revokeObjectURL(url);
        } catch (e) {}
      });
      audioQueueRef.current.clear();
    }
    
    // ✅ DON'T CLEAR TRANSCRIPT - Keep it visible!
    // ✅ DON'T set transcript to shimmer
    
    await new Promise(resolve => setTimeout(resolve, 100));
    
    log('⏸️ Pause complete - transcript still visible');
    
    // ✅ Reset shouldStop flag (so resume works)
    shouldStopRef.current = false;
    
  } else {
    // START
    log('▶️ Starting station...');
    shouldStopRef.current = false;
    isPausedRef.current = false;
    setIsPaused(false);
    
    runContinuousStation();
  }
};

  const nuclearStopAllAudio = async () => {
    log('🔴 NUCLEAR STOP: Killing all audio...');
    
    if (currentAudioRef.current) {
      try {
        const audio = currentAudioRef.current;
        audio.pause();
        audio.currentTime = 0;
        audio.volume = 0;
        audio.src = '';
        audio.load();
        currentAudioRef.current = null;
      } catch (e) {
        currentAudioRef.current = null;
      }
    }
    
    if (intermissionAudioRef.current) {
      try {
        const audio = intermissionAudioRef.current;
        audio.pause();
        audio.currentTime = 0;
        audio.volume = 0;
        audio.src = '';
        audio.load();
        intermissionAudioRef.current = null;
      } catch (e) {
        intermissionAudioRef.current = null;
      }
    }
    
    await new Promise(resolve => setTimeout(resolve, 300));
  };

const fadeOutMusicIntoHosts = async (audioElement, fadeOutDuration = 600) => {
  if (!audioElement) return;
  
  return new Promise((resolve) => {
    const startVolume = audioElement.volume;
    const steps = 50;
    const volumeStep = startVolume / steps;
    const timeStep = fadeOutDuration / steps;
    let currentStep = 0;
    
    const fadeInterval = setInterval(() => {
      if (currentStep >= steps || shouldStopRef.current) {
        audioElement.pause();
        audioElement.volume = 0;
        audioElement.currentTime = 0;
        activeAudioElements.current.delete(audioElement); // ✅ Remove from guest list
        currentAudioRef.current = null;
        clearInterval(fadeInterval);
        resolve();
        return;
      }
      
      if (audioElement) {
        audioElement.volume = Math.max(0, startVolume - (volumeStep * currentStep));
      }
      currentStep++;
    }, timeStep);
  });
};


  const getTimeOfDay = () => {
    const hour = new Date().getHours();
    if (hour >= 5 && hour < 12) return 'morning';
    if (hour >= 12 && hour < 18) return 'afternoon';
    return 'evening';
  };

  const selectRandomGuest = () => {
    const guests = [
      { name: 'Kenny', voice: 'guest1' },
      { name: 'Jackie', voice: 'guest2' }
    ];
    return guests[Math.floor(Math.random() * guests.length)];
  };

  const shouldIncludeGuest = (cycleNumber) => {
    if (cycleNumber === 0) return false;
    return Math.random() < 0.6;
  };

  // ✅ Run news cycle
const runNewsCycle = async () => {
  try {
    log(`\n🔄 Starting news cycle ${currentCycle + 1}`);
    setCurrentStatus('Gathering news...');
    
    // ✅ RESET ERROR COUNT on new cycle start
    cycleErrorCountRef.current = 0;
    setCycleErrorCount(0);
    
    setIsLoadingCards(true);
    setIsSearchingComplete(true);
    
    const musicPromise = playIntermissionMusicWithFade();
    const musicStartTime = Date.now();
    const minMusicDuration = 6000;

    // Fetch news
    const news = await fetchNewsArticles();

    if (shouldStopRef.current) return;

    const includeGuest = shouldIncludeGuest();
    const guest = includeGuest ? selectRandomGuest() : null;

    if (includeGuest && guest) {
      setHasGuest(true);
      setGuestName(guest.name);
      log(`🎙️ Guest ${guest.name} will join cycle ${currentCycle + 1}`);
    } else {
      setHasGuest(false);
      setGuestName('');
      log(`🎙️ No guest for cycle ${currentCycle + 1}`);
    }

    const scriptPromise = generateStationScript(news, includeGuest, guest?.name || '', false);

    log('🚀 STARTING AGGRESSIVE FIRST HOST AUDIO GENERATION...');

    const firstHostAudioPromise = (async () => {
      const script = await scriptPromise;
      const lines = script.split('\n').filter(line => line.trim());
      
      let firstHostVoice = null;
      let firstHostText = null;
      
      for (let i = 0; i < lines.length; i++) {
        const line = lines[i];
        const guideMatch = line.match(/Guide:\s*(.+)/i);
        const companionMatch = line.match(/Companion:\s*(.+)/i);

        if (guideMatch) {
          firstHostVoice = 'guide';
          firstHostText = guideMatch[1].trim();
          break;
        } else if (companionMatch) {
          firstHostVoice = 'therapist';
          firstHostText = companionMatch[1].trim();
          break;
        }
      }

      if (firstHostVoice && firstHostText) {
        log(`🎙️ Generating FIRST HOST AUDIO: ${firstHostVoice}`);
        
        const audioBlob = await SecureAPI.callHumeTTSSecure(
          firstHostText,
          VOICE_CONFIG[firstHostVoice],
          playbackSpeedRef.current
        );

        const trimmedBlob = await trimSilenceAggressive(audioBlob);
        const audioUrl = URL.createObjectURL(trimmedBlob);
        
        const cacheKey = `${firstHostVoice}-${firstHostText}`;
        audioQueueRef.current.set(cacheKey, { url: audioUrl, text: firstHostText, voice: firstHostVoice, used: false });
        
        log(`✅ FIRST HOST AUDIO READY AND CACHED`);
        return { ready: true, script };
      }
      
      return { ready: false, script };
    })();

    const elapsedTime = Date.now() - musicStartTime;
    if (elapsedTime < minMusicDuration) {
      await new Promise(resolve => setTimeout(resolve, minMusicDuration - elapsedTime));
    }

    const { script } = await firstHostAudioPromise;

    if (shouldStopRef.current) return;

    setIsLoadingCards(false);
    
    const cards = news.map((article, idx) => ({
      id: idx,
      articleId: article.id,
      title: article.title.split(' ').slice(0, 4).join(' '),
      fullTitle: article.title,
      image: article.imageUrl || '/image/studycard7.png',
      source: article.source,
      url: article.url,
      time: article.time,
      isReady: false,
      isActive: false,
      snippet: article.snippet
    }));
    
    setNewsCards(cards);
    
    await nuclearStopAllAudio();
    await new Promise(resolve => setTimeout(resolve, 300));
    
    setTranscript([]);
    setCurrentStatus('Broadcasting...');
    
    setCurrentCardIndex(0);
    completedCardsRef.current.clear();
    setCompletedCards(new Set());
    
    await playScriptWithCards(script, news);
    
    if (shouldStopRef.current) return;
    
    setCurrentCycle(prev => prev + 1);
    
  } catch (error) {
    log(`❌ Cycle error: ${error.message}`);
    
    // ✅ CIRCUIT BREAKER: TRACK CYCLE ERRORS
    cycleErrorCountRef.current++;
    setCycleErrorCount(cycleErrorCountRef.current);
    
    log(`⚠️ Cycle error ${cycleErrorCountRef.current}/${MAX_CYCLE_ERRORS}`);
    
    // ✅ CHECK ERROR SOURCE AND PROVIDE USER MESSAGE
    const errorMsg = error.message.toLowerCase();
    let userMessage = '';
    
    if (errorMsg.includes('no articles')) {
      userMessage = '❌ No news articles available. Retrying...';
    } else if (errorMsg.includes('fetch')) {
      userMessage = '❌ Failed to fetch news. Check connection.';
    } else if (errorMsg.includes('script')) {
      userMessage = '❌ Script generation failed. Retrying...';
    } else {
      userMessage = '❌ News cycle error. Retrying...';
    }
    
    // ✅ STOP AFTER MAX CYCLE ERRORS
    if (cycleErrorCountRef.current >= MAX_CYCLE_ERRORS) {
      log(`🔴 CYCLE CIRCUIT BREAKER - Stopping to save costs`);
      
      shouldStopRef.current = true;
      setIsPlaying(false);
      
      setTranscript([{
        voice: 'system',
        text: `${userMessage} Station stopped.`,
        timestamp: Date.now()
      }]);
      
      return;
    }
    
    // Wait before retrying (exponential backoff)
    const waitTime = 3000 * cycleErrorCountRef.current;
    log(`⏳ Retrying in ${waitTime / 1000} seconds...`);
    
    setTranscript([{
      voice: 'system',
      text: `${userMessage}`,
      timestamp: Date.now()
    }]);
    
    await new Promise(resolve => setTimeout(resolve, waitTime));
    return runNewsCycle();
  }
};



// ============================================
// HELPER: Smart source grouping
// ============================================
const getSourcesDescription = (sources) => {
  if (!sources || sources.length === 0) return 'your news sources';
  if (sources.length === 1) return sources[0].toLowerCase();
  if (sources.length === 2) return `${sources[0].toLowerCase()} and ${sources[1].toLowerCase()}`;
  return 'your selected news sources';
};

// ============================================
// SMOOTH INTRO GENERATION WITH TRENDING TOPICS
// ============================================
const generateDynamicIntro = async (selectedSources, userName, timeOfDay, recentTrends) => {
  const sourcesDescription = getSourcesDescription(selectedSources);
  
  // ✅ Build detailed news context for AI to summarize themes
  const newsContext = recentTrends && recentTrends.length > 0
    ? recentTrends.slice(0, 5).map((item, idx) => 
        `${idx + 1}. ${item.title}\nSource: ${item.source}`
      ).join('\n\n')
    : '';

  const introPrompt = `You are a warm, conversational news host greeting a listener.

**LISTENER:** ${userName}
**TIME OF DAY:** ${timeOfDay}
**SOURCES:** ${selectedSources.map(s => s.toUpperCase()).join(', ')}

**RECENT NEWS HEADLINES:**
${newsContext || 'General news coverage'}

❌ NEVER use exclamation marks (!)
❌ Don't be flat or monotone
❌ Avoid robotic delivery

Your task: Create a natural, engaging intro (2-3 sentences) that:
- Greets ${userName} warmly by name
- Acknowledges the time of day casually
- SUMMARIZES the main THEMES in the news (NOT listing exact titles)
- Identifies patterns: is there tension between countries? Tech developments? Political shifts? Economic changes?
- Ends with something like "give us a moment to pull that together" or "we'll get started in just a sec"
- Sounds like a real person casually mentioning what's been happening
- NO corporate speak, NO reading titles verbatim

**CRITICAL - IDENTIFY THEMES, NOT TITLES:**
❌ BAD: "There's news about Tesla stock and China trade and Elon Musk tweets"
✅ GOOD: "There's been a lot happening with tech companies and international trade tensions"

❌ BAD: "We have stories from CNN, TechCrunch, and ABC"
✅ GOOD: "There's some interesting developments in politics and technology"

**EXAMPLES OF GREAT INTROS:**

Example 1 (Tech + Politics theme):
"Hey ${userName}, good ${timeOfDay}. So there's been quite a bit of buzz around tech regulation and what's happening in Washington—some pretty significant moves. Give us just a moment to get all that sorted for you."

Example 2 (International + Business theme):
"Good ${timeOfDay} ${userName}. Lot of talk about trade tensions between major economies and how that's affecting markets. We'll pull those details together in just a sec."

Example 3 (Multiple themes):
"${userName}, good ${timeOfDay}. There's some interesting stuff happening—political shifts, tech developments, and economic changes all kind of intersecting right now. Hang tight while we gather those updates."

Example 4 (Breaking news feel):
"Hey ${userName}, things have been moving fast today—major announcements from tech leaders and some significant policy changes. Let us get that ready for you real quick."

Example 5 (Casual urgency):
"Good ${timeOfDay} ${userName}. So we've got developments on multiple fronts—international relations heating up, tech sector making moves, economy showing some shifts. Give us a moment to pull it all together."

**TONE:** Natural, conversational, like a friend giving a heads-up about what's been happening. Identify the BIG PICTURE themes, not individual story titles.

Output ONLY the greeting text. No quotes, no preamble, just the raw spoken text.`;

  try {
    const response = await SecureAPI.callAzureOpenAI(
      [
        {
          role: 'system',
          content: 'You are a conversational news host who identifies themes and patterns in news, not just lists headlines. Sound natural and engaging. Summarize what types of stories are happening (tech, politics, economy, international) rather than naming specific articles. Output only raw greeting text.'
        },
        { role: 'user', content: introPrompt }
      ],
      {
        temperature: 0.92,
        max_tokens: 120
      }
    );
    
    if (!response.ok) {
      log(`⚠️ Intro generation failed: ${response.status}`);
      throw new Error('Failed to generate intro');
    }
    
    const data = await response.json();
    
    // ✅ BETTER PARSING - handle various response formats
    let introText = data.choices[0].message.content;
    
    // Log raw response for debugging
    log(`🔍 Raw intro response: "${introText}"`);
    
    // Clean up the response
    introText = introText
      .trim()
      .replace(/^["']|["']$/g, '')  // Remove quotes at start/end
      .replace(/^`+|`+$/g, '')      // Remove backticks
      .replace(/\\n/g, ' ')         // Replace escaped newlines
      .replace(/\n/g, ' ')          // Replace actual newlines
      .trim();
    
    // Verify it's not empty after cleaning
    if (!introText || introText.length < 10) {
      log(`⚠️ Intro too short after cleaning: "${introText}"`);
      throw new Error('Intro generation returned empty/invalid text');
    }
    
    log(`✅ Cleaned intro: "${introText}"`);
    return introText;
    
} catch (error) {
    log(`⚠️ Intro generation error: ${error.message}`);
    
    // Fallback intro
    const timeGreeting = timeOfDay === 'morning' ? 'Good morning' : 
                        timeOfDay === 'afternoon' ? 'Good afternoon' : 
                        'Good evening';
    
    const sourcesDesc = getSourcesDescription(selectedSources);
    
    return `${timeGreeting} ${userName}, there's quite a bit happening across ${sourcesDesc} right now. Give us just a moment as we get that ready for you.`;
  }
};

// ============================================
// FIX: TRANSCRIPT SYNCED WITH PLAYBACK (Shows current, not previous)
// ============================================
const playAudio = async (text, voice) => {
  // ✅ VALIDATE INPUT
  if (!text || typeof text !== 'string' || text.trim() === '') {
    log(`⚠️ Skipping empty text for voice: ${voice}`);
    return;
  }
  
  if (!voice || !VOICE_CONFIG[voice]) {
    log(`⚠️ Invalid voice: ${voice}`);
    return;
  }
  
  if (shouldStopRef.current) return;

  const cacheKey = `${voice}-${text}`;
  const cached = audioQueueRef.current.get(cacheKey);

  // ✅ USE CACHED AUDIO
  if (cached && !cached.used) {
    cached.used = true;
    log(`⚡ Playing from cache: ${voice}`);

    return new Promise((resolve) => {
      if (shouldStopRef.current) {
        resolve();
        return;
      }
      
      const audio = new Audio(cached.url);
      
      if (currentAudioRef.current) {
        activeAudioElements.current.delete(currentAudioRef.current);
        currentAudioRef.current.pause();
      }
      
      activeAudioElements.current.add(audio);
      currentAudioRef.current = audio;
      
      audio.playbackRate = playbackSpeedRef.current;
      audio.volume = 1;

      audio.onplay = () => {
        if (!shouldStopRef.current) {
          // ✅ RESET ERROR COUNT ON SUCCESS
          ttsErrorCountRef.current = 0;
          setTtsErrorCount(0);
          
          setTranscript(prev => {
            const lastEntry = prev[prev.length - 1];
            if (lastEntry && lastEntry.text === text && lastEntry.voice === voice) {
              return prev;
            }
            return [...prev, { voice, text, timestamp: Date.now() }];
          });
        }
      };

      audio.onended = () => {
        activeAudioElements.current.delete(audio);
        currentAudioRef.current = null;
        URL.revokeObjectURL(cached.url);
        audioQueueRef.current.delete(cacheKey);
        resolve();
      };

      audio.onerror = () => {
        activeAudioElements.current.delete(audio);
        currentAudioRef.current = null;
        resolve();
      };

      audio.play().catch(err => {
        log(`⚠️ Cached audio play error: ${err.message}`);
        activeAudioElements.current.delete(audio);
        currentAudioRef.current = null;
        resolve();
      });
    });
  }

  // ✅ GENERATE LIVE IF NOT CACHED
  try {
    if (shouldStopRef.current) return;
    
    let finalText = text;
    if (text.length > 3000) {
      log(`⚠️ Text too long (${text.length} chars) - truncating`);
      finalText = text.substring(0, 3000);
    }
    
    log(`🎙️ Generating audio (Octave 2): ${voice}`);
    
    // ✅ CALL HUME WITH OCTAVE 2 (SecureAPI handles it)
    const audioBlob = await SecureAPI.callHumeTTSSecure(
      finalText,
      VOICE_CONFIG[voice],
      playbackSpeedRef.current
    );

    const trimmedBlob = await trimSilenceAggressive(audioBlob);
    const audioUrl = URL.createObjectURL(trimmedBlob);

    return new Promise((resolve) => {
      if (shouldStopRef.current) {
        URL.revokeObjectURL(audioUrl);
        resolve();
        return;
      }
      
      const audio = new Audio(audioUrl);
      
      if (currentAudioRef.current) {
        activeAudioElements.current.delete(currentAudioRef.current);
        currentAudioRef.current.pause();
      }
      
      activeAudioElements.current.add(audio);
      currentAudioRef.current = audio;
      
      audio.playbackRate = playbackSpeedRef.current;
      audio.volume = 1;

      audio.onplay = () => {
        if (!shouldStopRef.current) {
          // ✅ RESET ERROR COUNT ON SUCCESS
          ttsErrorCountRef.current = 0;
          setTtsErrorCount(0);
          
          setTranscript(prev => {
            const lastEntry = prev[prev.length - 1];
            if (lastEntry && lastEntry.text === text && lastEntry.voice === voice) {
              return prev;
            }
            return [...prev, { voice, text, timestamp: Date.now() }];
          });
        }
      };

      audio.onended = () => {
        activeAudioElements.current.delete(audio);
        currentAudioRef.current = null;
        URL.revokeObjectURL(audioUrl);
        resolve();
      };

      audio.onerror = () => {
        log(`⚠️ Generated audio play error`);
        activeAudioElements.current.delete(audio);
        currentAudioRef.current = null;
        URL.revokeObjectURL(audioUrl);
        resolve();
      };
      
      audio.play().catch(err => {
        log(`⚠️ Generated audio play error: ${err.message}`);
        activeAudioElements.current.delete(audio);
        currentAudioRef.current = null;
        URL.revokeObjectURL(audioUrl);
        resolve();
      });
    });
    
  } catch (error) {
    log(`❌ Audio generation error: ${error.message}`);
    
    // ✅ CIRCUIT BREAKER: TRACK CONSECUTIVE ERRORS
    ttsErrorCountRef.current++;
    setTtsErrorCount(ttsErrorCountRef.current);
    
    log(`⚠️ TTS error count: ${ttsErrorCountRef.current}/${MAX_TTS_ERRORS}`);
    
    // ✅ CHECK ERROR SOURCE AND PROVIDE USER MESSAGE
    const errorMsg = error.message.toLowerCase();
    let userMessage = '';
    
    if (errorMsg.includes('400') || errorMsg.includes('invalid')) {
      userMessage = '❌ Audio config error. Check Hume API key and voice IDs.';
      log('🔴 400 Error - Invalid voice ID or configuration');
    } else if (errorMsg.includes('401') || errorMsg.includes('unauthorized')) {
      userMessage = '❌ Authentication failed. Check Hume API key.';
      log('🔴 401 Error - API key invalid');
    } else if (errorMsg.includes('429') || errorMsg.includes('rate')) {
      userMessage = '⚠️ Rate limited. Waiting before retry...';
      log('🟡 429 Error - Rate limit hit');
    } else if (errorMsg.includes('timeout') || errorMsg.includes('network')) {
      userMessage = '⚠️ Network error. Check connection.';
      log('🟡 Network error - Timeout or no connection');
    } else {
      userMessage = '❌ Audio service unavailable. Retrying...';
      log('🔴 Unknown error');
    }
    
    // ✅ CIRCUIT BREAKER: STOP AFTER MAX ERRORS
    if (ttsErrorCountRef.current >= MAX_TTS_ERRORS) {
      log(`🔴 CIRCUIT BREAKER ACTIVATED - Too many failures`);
      
      shouldStopRef.current = true;
      setIsPlaying(false);
      
      // Show detailed error to user
      setTranscript([{
        voice: 'system',
        text: `${userMessage} Station stopped to save costs.`,
        timestamp: Date.now()
      }]);
      
      return;
    }
    
    // ✅ SHOW WARNING BUT CONTINUE (with appropriate wait)
    if (errorMsg.includes('429')) {
      // Rate limited - wait longer
      log('⏳ Backing off due to rate limit...');
      await new Promise(resolve => setTimeout(resolve, 5000));
    } else {
      // Other errors - brief wait
      await new Promise(resolve => setTimeout(resolve, 1000));
    }
    
    // Don't throw - let it continue (will appear as silence)
  }
};

const runContinuousStation = async () => {
  log('=== STARTING SELECTED NEWS STATION (OCTAVE 2) ===');
  
  try {
    localStorage.removeItem(`selected_news_session_${userName}`);
  } catch (error) {
    log(`⚠️ Could not clear old session`);
  }
  
  shouldStopRef.current = false;
  isPausedRef.current = false;
  setIsPaused(false);
  
  setIsPlaying(true);
  setTranscript([]);
  
  // ✅ RESET ERROR COUNTERS
  ttsErrorCountRef.current = 0;
  cycleErrorCountRef.current = 0;
  setTtsErrorCount(0);
  setCycleErrorCount(0);
  
  try {
    log('🎵 Playing intro music...');

    setTranscript([{
      voice: 'system',
      text: `One sec, ${userName}...`,
      timestamp: Date.now(),
      isSearching: false
    }]);

    setIsLoadingCards(false);
    setIsSearchingComplete(false);

    const introMusicPromise = playIntroMusicWithFade(10);

    const timeOfDay = getTimeOfDay();
    log('🎙️ Generating dynamic intro...');

    let trendingNews = [];
    try {
      trendingNews = await fetchNewsArticles();
    } catch (error) {
      log(`⚠️ Could not fetch trends`);
    }

    const introAudioPromise = (async () => {
      try {
        let introText = await generateDynamicIntro(selectedSources, userName, timeOfDay, trendingNews);
        
        if (!introText || introText.trim() === '' || introText === '""' || introText === "''") {
          log('⚠️ Empty intro generated, using fallback');
          const timeGreeting = timeOfDay === 'morning' ? 'Good morning' : 
                              timeOfDay === 'afternoon' ? 'Good afternoon' : 
                              'Good evening';
          
          const sourcesDesc = getSourcesDescription(selectedSources);
          
          introText = `${timeGreeting} ${userName}, we've got some major updates from ${sourcesDesc}. Give us just a moment as we pull those together for you.`;
        }
        
        log(`✅ Final intro text: "${introText.substring(0, 50)}..."`);
        
        const audioBlob = await SecureAPI.callHumeTTSSecure(
          introText,
          VOICE_CONFIG['guide'],
          playbackSpeedRef.current
        );

        const trimmedBlob = await trimSilenceAggressive(audioBlob);
        const audioUrl = URL.createObjectURL(trimmedBlob);
        
        const cacheKey = `guide-${introText}`;
        audioQueueRef.current.set(cacheKey, { url: audioUrl, text: introText, voice: 'guide', used: false });
        
        log(`✅ INTRO AUDIO READY AND CACHED`);
        return { ready: true, text: introText };
        
      } catch (introError) {
        log(`⚠️ Intro generation failed: ${introError.message}`);
        
        // ✅ FALLBACK: Skip to news without intro
        const timeGreeting = timeOfDay === 'morning' ? 'Good morning' : 
                            timeOfDay === 'afternoon' ? 'Good afternoon' : 
                            'Good evening';
        const sourcesDesc = getSourcesDescription(selectedSources);
        const fallbackText = `${timeGreeting} ${userName}. Let's get started.`;
        
        return { ready: false, text: fallbackText, isError: true };
      }
    })();

    await introMusicPromise;

    if (shouldStopRef.current) {
      setIsPlaying(false);
      return;
    }

    setTranscript([]);

    const introStatus = await introAudioPromise;

    if (shouldStopRef.current) {
      setIsPlaying(false);
      return;
    }

    // ✅ PLAY INTRO (or skip if error)
    if (!introStatus.isError) {
      log(`🎤 Playing intro audio...`);
      await playAudio(introStatus.text, 'guide');

      if (shouldStopRef.current) {
        setIsPlaying(false);
        return;
      }

      await new Promise(resolve => setTimeout(resolve, 800));
    } else {
      log(`⚠️ Skipping intro due to error, jumping to news`);
    }

    setTranscript([{
      voice: 'system',
      text: 'Searching for fresh news...',
      timestamp: Date.now(),
      isSearching: true
    }]);
    
    // Continue with news cycle...
    while (!shouldStopRef.current) {
      await runNewsCycle();
      
      if (shouldStopRef.current) break;
      
      await nuclearStopAllAudio();
      
      let attempts = 0;
      while ((currentAudioRef.current !== null || intermissionAudioRef.current !== null) && attempts < 3) {
        await nuclearStopAllAudio();
        await new Promise(resolve => setTimeout(resolve, 200));
        attempts++;
      }
      
      setTranscript([]);
      
      if (shouldStopRef.current) break;
    }
    
  } catch (error) {
    log(`❌ Station error: ${error.message}`);
    
    setTranscript([]);
    
    const errorMsg = error.message.toLowerCase();
    let displayError = '❌ Station error';
    
    if (errorMsg.includes('fetch')) {
      displayError = '❌ Network connection error';
    } else if (errorMsg.includes('auth')) {
      displayError = '❌ Authentication error - check API key';
    } else if (errorMsg.includes('400')) {
      displayError = '❌ Configuration error - check voice IDs';
    }
    
    setTranscript([{
      voice: 'system',
      text: `${displayError}. Please try again.`,
      timestamp: Date.now()
    }]);
    
    setIsPlaying(false);
  }
};


// ============================================
// GENERATE NATURAL CARD TRANSITION
// ============================================
const generateCardTransition = async (fromCard, toCard, isNext = true) => {
  const direction = isNext ? 'next' : 'previous';
  const action = isNext ? 'moving on to' : 'going back to';
  
  const transitionPrompt = `You are a natural news host transitioning between stories.

**FROM:** ${fromCard.fullTitle}
**TO:** ${toCard.fullTitle}
**TO SOURCE:** ${toCard.source}
**DIRECTION:** ${direction === 'next' ? 'forward to next story' : 'back to previous story'}

Create a BRIEF, natural transition (1-2 sentences, 15-25 words) that:
- Acknowledges where we're ${direction === 'next' ? 'going' : 'coming from'}
- Mentions ${toCard.source} casually
- Hints at the topic without spoiling it
- Sounds like a real person, not scripted
- NO "let's" or "so" starts
- Use contractions (it's, that's, etc)

**EXAMPLES:**
- "Alright, moving on—${toCard.source} has some huge developments we need to unpack here."
- "Yeah okay, next up from ${toCard.source}, there's something pretty significant happening."
- "Going back to what ${toCard.source} reported, this is actually pretty important."
- "${toCard.source} just broke something interesting—let's dig into it."

Output ONLY the transition text, nothing else.`;


  try {
    // ✅ CHANGED: Use SecureAPI
    const response = await SecureAPI.callAzureOpenAI(
      [
        {
          role: 'system',
          content: 'You create smooth, natural news transitions. Sound like a real person. Keep it brief and conversational.'
        },
        { role: 'user', content: transitionPrompt }
      ],
      {
        temperature: 0.85,
        max_tokens: 50
      }
    );

    return response.choices[0].message.content.trim().replace(/^["']|["']$/g, '');
  } catch (error) {
    // Fallback
    const fallbacks = [
      `Alright, ${isNext ? 'moving on to' : 'back to'} ${toCard.source}—there's something important here.`,
      `${toCard.source} has this ${isNext ? 'next' : 'earlier'}—let's take a look.`,
      `Okay, so ${isNext ? 'next up' : 'before that'} from ${toCard.source}, here's what's happening.`
    ];
    return fallbacks[Math.floor(Math.random() * fallbacks.length)];
  }
};

// ============================================
// FIX: PROPER CARD SYNCING WITH NATURAL TRANSITIONS
// ============================================
const playScriptWithCards = async (script, newsItems) => {
  if (!script || typeof script !== 'string') {
    log(`❌ Invalid script provided: ${typeof script}`);
    return;
  }
  
  const lines = script.split('\n').filter(line => line.trim());
  scriptLinesRef.current = lines;
  
  log(`📝 Script has ${lines.length} lines for ${newsItems.length} stories${hasGuest ? ` with guest ${guestName}` : ''}`);
  
  // ✅ BUILD PRECISE CARD MAP (which lines belong to which card)
  const cardMap = {};
  const linesPerCard = Math.floor(lines.length / newsItems.length);
  
  for (let i = 0; i < newsItems.length; i++) {
    const startLine = i * linesPerCard;
    const endLine = i === newsItems.length - 1 ? lines.length : (i + 1) * linesPerCard;
    
    cardMap[i] = { start: startLine, end: endLine };
    log(`📌 Card ${i}: lines ${startLine}-${endLine}`);
  }
  
  setScriptToCardMap(cardMap);
  
  let lastNewsIndex = -1;
  let guestIntroduced = false;
  currentLineIndexRef.current = 0;
  
  for (let i = 0; i < lines.length; i++) {
    currentLineIndexRef.current = i;
    
    if (shouldStopRef.current) break;
    
    while (isPausedRef.current && !shouldStopRef.current) {
      await new Promise(resolve => setTimeout(resolve, 50));
    }
    
    if (shouldStopRef.current) break;
    
    // ✅ PERFECT CARD DETECTION - Which card does this line belong to?
    let newsIndex = 0;
    for (let cardIdx = 0; cardIdx < newsItems.length; cardIdx++) {
      const range = cardMap[cardIdx];
      if (i >= range.start && i < range.end) {
        newsIndex = cardIdx;
        break;
      }
    }
    
    // ✅ INSTANT CARD SWITCH when we detect a new card
    if (newsIndex !== lastNewsIndex) {
      log(`🎯 CARD SWITCH: Line ${i} → Card ${newsIndex}`);
      
      // Mark previous card as completed
      if (lastNewsIndex >= 0 && newsCards[lastNewsIndex]) {
        completedCardsRef.current.add(newsCards[lastNewsIndex].id);
        setCompletedCards(new Set(completedCardsRef.current));
      }
      
      const prevIndex = lastNewsIndex;
      lastNewsIndex = newsIndex;
      
      // ✅ IMMEDIATE UI UPDATE (no delay)
      setCurrentCardIndex(newsIndex);
      
      setNewsCards(prev => prev.map((card, idx) => ({
        ...card,
        isActive: idx === newsIndex,
        isReady: idx <= newsIndex
      })));
      
      // ✅ INSTANT SCROLL (no setTimeout)
      if (newsCardsRef.current?.children[newsIndex]) {
        newsCardsRef.current.children[newsIndex].scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'center'
        });
      }
      
      log(`✅ Card synced instantly: ${newsIndex + 1}/${newsItems.length}`);
      
      // ✅ Natural transition (only after first card)
      if (prevIndex >= 0 && prevIndex !== newsIndex) {
        const fromCard = newsCards[prevIndex];
        const toCard = newsCards[newsIndex];
        
        if (fromCard && toCard) {
          const isNext = newsIndex > prevIndex;
          const transitionText = await generateCardTransition(fromCard, toCard, isNext);
          log(`🎙️ Transition: "${transitionText}"`);
          
          const transitionHost = Math.random() < 0.5 ? 'guide' : 'therapist';
          await playAudio(transitionText, transitionHost);
          
          if (shouldStopRef.current) break;
          await new Promise(resolve => setTimeout(resolve, 200));
        }
      }
    }
    
    const line = lines[i];
    const guideMatch = line.match(/Guide:\s*(.+)/i);
    const companionMatch = line.match(/Companion:\s*(.+)/i);
    const guestMatch = hasGuest && guestName
      ? line.match(new RegExp(`${guestName}:\\s*(.+)`, 'i'))
      : null;

    // Guest introduction logic (unchanged)
    if (guestMatch && !guestIntroduced && hasGuest) {
      guestIntroduced = true;
      log(`🎙️ Generating guest introduction for ${guestName}...`);
      
      const recentLines = lines.slice(Math.max(0, i - 3), i).join('\n');
      const currentNewsItem = newsItems[newsIndex];
      const currentTopic = currentNewsItem?.title || 'the news';
      
      const guestWelcomePrompt = `You are a podcast host. A guest named ${guestName} just started speaking.

**CONTEXT:**
${recentLines}

**TOPIC:** ${currentTopic}

Create a BRIEF acknowledgment (1 sentence, 8-12 words) that:
- Acknowledges ${guestName} casually
- Stays focused on the topic
- NO "I" statements
- Sounds natural

**GOOD:**
- "Oh hey ${guestName}, what's your take?"
- "${guestName}, what are the latest reports saying?"

Output ONLY the acknowledgment.`;


  let welcomeText;
  try {
    // ✅ CHANGED: Use SecureAPI
    const response = await SecureAPI.callAzureOpenAI(
      [
        { 
          role: 'system', 
          content: 'Create casual, brief guest acknowledgments. NO formal welcomes. Under 12 words.'
        },
        { role: 'user', content: guestWelcomePrompt }
      ],
      {
        temperature: 0.9,
        max_tokens: 40
      }
    );
    
    welcomeText = response.choices[0].message.content.trim().replace(/^["']|["']$/g, '');
        const bannedPhrases = ['perfect timing', 'just in time', 'welcome', 'glad you could join'];
        if (bannedPhrases.some(phrase => welcomeText.toLowerCase().includes(phrase))) {
          welcomeText = `Hey ${guestName}, what's your take?`;
        }
        
        log(`✅ Generated welcome: "${welcomeText}"`);
      } catch (error) {
        log(`⚠️ Welcome generation failed, using fallback`);
        const fallbacks = [
          `Hey ${guestName}, what do you think?`,
          `${guestName}, what's your take?`,
          `${guestName}, thoughts on this?`
        ];
        welcomeText = fallbacks[Math.floor(Math.random() * fallbacks.length)];
      }
      
      const welcomeHost = Math.random() < 0.5 ? 'guide' : 'therapist';
      await playAudio(welcomeText, welcomeHost);
      
      if (shouldStopRef.current) break;
      await new Promise(resolve => setTimeout(resolve, 150));
    }

    if (guideMatch || companionMatch || guestMatch) {
      let voice, text;
      
      if (guideMatch) {
        voice = 'guide';
        text = guideMatch[1].trim();
      } else if (companionMatch) {
        voice = 'therapist';
        text = companionMatch[1].trim();
      } else if (guestMatch) {
        voice = guestName === 'Kenny' ? 'guest1' : 'guest2';
        text = guestMatch[1].trim();
      }

      // Preload next speaker
     if (i + 1 < lines.length) {
        const nextLine = lines[i + 1];
        const nextGuideMatch = nextLine.match(/Guide:\s*(.+)/i);
        const nextCompanionMatch = nextLine.match(/Companion:\s*(.+)/i);
        const nextGuestMatch = hasGuest && guestName
          ? nextLine.match(new RegExp(`${guestName}:\\s*(.+)`, 'i'))
          : null;

        let nextVoice, nextText;
        
        if (nextGuideMatch) {
          nextVoice = 'guide';
          nextText = nextGuideMatch[1].trim();
        } else if (nextCompanionMatch) {
          nextVoice = 'therapist';
          nextText = nextCompanionMatch[1].trim();
        } else if (nextGuestMatch) {
          nextVoice = guestName === 'Kenny' ? 'guest1' : 'guest2';
          nextText = nextGuestMatch[1].trim();
        }

        if (nextVoice && nextText) {
          void preloadAudio(nextText, nextVoice); // ✅ Use preloadAudio (not smartPreloadAudio)
        }
      }

      // ✅ PLAY CURRENT AUDIO
      await playAudio(text, voice);
      
      if (shouldStopRef.current) break;
    }
  }
  
  // Mark final card as completed
  if (lastNewsIndex >= 0 && newsCards[lastNewsIndex]) {
    completedCardsRef.current.add(newsCards[lastNewsIndex].id);
    setCompletedCards(new Set(completedCardsRef.current));
  }
  
  log(`✅ Script complete`);
};



const playIntroMusicWithFade = async (duration = 5) => {
  try {
    // ✅ CHECK if paused/stopped BEFORE starting
    if (shouldStopRef.current || isPausedRef.current) {
      log('⏸️ Skipping intro music - paused/stopped');
      return;
    }
    
    const musicUrl = selectRandomMusicNeverRepeat(INTRO_MUSIC_OPTIONS, 'Intro');
    const audio = new Audio(musicUrl);
    audio.volume = 0.4;
    audio.crossOrigin = 'anonymous';
    
    // ✅ REGISTER IN GUEST LIST
    activeAudioElements.current.add(audio);
    currentAudioRef.current = audio;
    
    return new Promise((resolve) => {
      audio.oncanplaythrough = () => {
        // ✅ CHECK AGAIN before playing
        if (shouldStopRef.current || isPausedRef.current) {
          activeAudioElements.current.delete(audio);
          currentAudioRef.current = null;
          log('⏸️ Cancelled intro music - paused');
          resolve();
          return;
        }
        
        audio.play().catch(e => log(`⚠️ Auto-play blocked: ${e.message}`));
        
        setTimeout(async () => {
          if (!shouldStopRef.current) {
            await fadeOutMusicIntoHosts(audio, 800);
          }
          activeAudioElements.current.delete(audio);
          resolve();
        }, duration * 1000);
      };
      
      audio.onerror = () => {
        log(`⚠️ Intro music failed to load`);
        activeAudioElements.current.delete(audio);
        resolve();
      };
      
      audio.load();
    });
  } catch (error) {
    log(`❌ Intro music error: ${error.message}`);
  }
};

const playIntermissionMusicWithFade = async () => {
  try {
    // ✅ CHECK if paused/stopped BEFORE starting
    if (shouldStopRef.current || isPausedRef.current) {
      log('⏸️ Skipping intermission music - paused/stopped');
      return;
    }
    
    const musicUrl = selectRandomMusicNeverRepeat(INTERMISSION_MUSIC_OPTIONS, 'Intermission');
    const audio = new Audio(musicUrl);
    audio.volume = 0.3;
    audio.loop = true;
    audio.crossOrigin = 'anonymous';
    
    // ✅ REGISTER IN GUEST LIST
    activeAudioElements.current.add(audio);
    currentAudioRef.current = audio;
    intermissionAudioRef.current = audio;
    
    return new Promise((resolve) => {
      audio.oncanplaythrough = () => {
        // ✅ CHECK AGAIN before playing
        if (shouldStopRef.current || isPausedRef.current) {
          activeAudioElements.current.delete(audio);
          currentAudioRef.current = null;
          intermissionAudioRef.current = null;
          log('⏸️ Cancelled intermission music - paused');
          resolve();
          return;
        }
        
        audio.play().catch(e => log(`⚠️ Auto-play blocked: ${e.message}`));
        resolve();
      };
      
      audio.onerror = () => {
        log(`⚠️ Intermission music failed to load`);
        activeAudioElements.current.delete(audio);
        intermissionAudioRef.current = null;
        resolve();
      };
      
      audio.load();
    });
  } catch (error) {
    log(`❌ Intermission music error: ${error.message}`);
    intermissionAudioRef.current = null;
  }
};
const handleClose = async () => {
  log('🔴 Closing station - COMPLETE SHUTDOWN');
  
  shouldStopRef.current = true;
  isPausedRef.current = true;
  
  // ✅ RESET ERROR COUNTERS
  ttsErrorCountRef.current = 0;
  cycleErrorCountRef.current = 0;
  setTtsErrorCount(0);
  setCycleErrorCount(0);
  
  await nuclearCleanupAudio();
  await new Promise(resolve => setTimeout(resolve, 500));
  
  setIsPlaying(false);
  setIsPaused(false);
  setTranscript([]);
  setNewsCards([]);
  setCurrentCardIndex(0);
  
  log('✅ Station closed - all audio stopped');
  
  onClose();
};



  const handleRefreshClick = () => {
    setShowRefreshConfirm(true);
  };

  const cancelRefresh = () => {
    setShowRefreshConfirm(false);
  };

  const handleArticleClick = (card) => {
    setSelectedArticle(card);
    setShowArticleConfirm(true);
  };

  const cancelArticle = () => {
    setShowArticleConfirm(false);
    setSelectedArticle(null);
  };

  const confirmArticle = () => {
    if (selectedArticle?.url) {
      window.open(selectedArticle.url, '_blank');
    }
    setShowArticleConfirm(false);
    setSelectedArticle(null);
  };

  const changeSpeed = () => {
    const speeds = [1.0, 1.15, 1.25, 1.5];
    const currentIndex = speeds.indexOf(playbackSpeed);
    const nextSpeed = speeds[(currentIndex + 1) % speeds.length];
    setPlaybackSpeed(nextSpeed);
  };
useEffect(() => {
  return () => {
    log('🧹 Component unmounting - nuclear cleanup');
    shouldStopRef.current = true;
    isPausedRef.current = true;
    
    // Abort all preloads (if exists)
    if (preloadAbortControllers.current) {
      preloadAbortControllers.current.forEach(controller => {
        try {
          controller.abort();
        } catch (e) {}
      });
      preloadAbortControllers.current.clear();
    }
    
    // Stop all tracked audio
    activeAudioElements.current.forEach(audio => {
      try {
        audio.pause();
        audio.currentTime = 0;
        audio.volume = 0;
        audio.src = '';
      } catch (e) {}
    });
    activeAudioElements.current.clear();
    
    // Clean up refs
    if (currentAudioRef.current) {
      currentAudioRef.current.pause();
      currentAudioRef.current = null;
    }
    
    if (intermissionAudioRef.current) {
      intermissionAudioRef.current.pause();
      intermissionAudioRef.current = null;
    }
    
    // Clean up cache (if exists)
    if (audioQueueRef.current) {
      audioQueueRef.current.forEach(({ url }) => {
        try {
          URL.revokeObjectURL(url);
        } catch (e) {}
      });
      audioQueueRef.current.clear();
    }
  };
}, []);
  useEffect(() => {
    playbackSpeedRef.current = playbackSpeed;
    if (currentAudioRef.current) {
      currentAudioRef.current.playbackRate = playbackSpeed;
    }
  }, [playbackSpeed]);

  useEffect(() => {
    if (transcriptEndRef.current && transcript.length > 0) {
      transcriptEndRef.current.scrollTop = 0;
    }
  }, [transcript]);

  // ============================================
// SHIMMER LOADING COMPONENT
// ============================================
const ShimmerLine = () => (
  <div style={{
    marginBottom: '20px',
    textAlign: 'left',
    animation: 'fadeInUp 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px'
  }}>
    <div style={{
      width: '8px',
      height: '8px',
      borderRadius: '50%',
      background: '#ffffff',
      marginTop: '8px',
      flexShrink: 0,
      animation: 'pulseGlow 1.5s infinite'
    }} />
    <div style={{
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      gap: '8px'
    }}>
      <div style={{
        height: '16px',
        borderRadius: '4px',
        background: 'rgba(255, 255, 255, 0.1)',
        width: '90%',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent)',
          animation: 'shimmer 1.5s infinite',
          backgroundSize: '200% 100%'
        }} />
      </div>
      <div style={{
        height: '16px',
        borderRadius: '4px',
        background: 'rgba(255, 255, 255, 0.1)',
        width: '75%',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent)',
          animation: 'shimmer 1.5s infinite 0.2s',
          backgroundSize: '200% 100%'
        }} />
      </div>
      <div style={{
        height: '16px',
        borderRadius: '4px',
        background: 'rgba(255, 255, 255, 0.1)',
        width: '85%',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent)',
          animation: 'shimmer 1.5s infinite 0.4s',
          backgroundSize: '200% 100%'
        }} />
      </div>
    </div>
  </div>
);
  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.5)',
      zIndex: 9999,
      display: 'flex',
      alignItems: 'flex-end',
      animation: 'fadeIn 0.25s ease'
    }}>
      <div style={{
        width: '100%',
        height: '92vh',
        backgroundColor: '#1a1a1a',
        borderRadius: '28px 28px 0 0',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        borderTop: '2px solid #8d8d8dff',
        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.4)',
        overflow: 'hidden',
        position: 'relative',
        display: 'flex',
        flexDirection: 'column'
      }}>
        {/* Gradient background */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'linear-gradient(135deg, #f65a3bff 0%, #f65c5fff 100%)',
          opacity: 0.3,
          zIndex: 0
        }} />
        
        {/* Header */}
        <div style={{
          padding: '24px',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          position: 'relative',
          zIndex: 2
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
            <div style={{
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              background: 'rgba(255, 255, 255, 0.1)',
              backdropFilter: 'blur(10px)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '2px solid rgba(255, 255, 255, 0.2)'
            }}>
              <Radio size={20} color="#fff" />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{
                fontSize: '16px',
                fontWeight: '600',
                color: '#fff',
                textAlign: 'left',
                marginBottom: '2px',
                textShadow: '0 2px 8px rgba(0, 0, 0, 0.2)'
              }}>
                Your Selected News
              </div>
              <div style={{
                fontSize: '12px',
                color: 'rgba(255, 255, 255, 0.7)',
                textAlign: 'left',
                background: currentStatus 
                  ? 'linear-gradient(90deg, rgba(255,255,255,0.4) 0%, rgba(255,255,255,0.6) 25%, rgba(255,255,255,1) 50%, rgba(255,255,255,0.6) 75%, rgba(255,255,255,0.4) 100%)'
                  : 'none',
                backgroundSize: currentStatus ? '200% 100%' : 'auto',
                backgroundClip: currentStatus ? 'text' : 'initial',
                WebkitBackgroundClip: currentStatus ? 'text' : 'initial',
                WebkitTextFillColor: currentStatus ? 'transparent' : 'rgba(255, 255, 255, 0.7)',
                animation: currentStatus ? 'textShimmer 3s linear infinite' : 'none'
              }}>
                {currentStatus || 'Live news station'}
              </div>
            </div>
          </div>
          
          <button
            onClick={handleClose}
            style={{
              width: '32px',
              height: '32px',
              borderRadius: '50%',
              background: 'rgba(255, 255, 255, 0.1)',
              border: 'none',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.2s'
            }}
          >
            <X size={18} color="#fff" />
          </button>
        </div>

        {/* Content Area */}
        <div 
          ref={transcriptEndRef}
          style={{
            flex: 1,
            position: 'relative',
            zIndex: 2,
            overflowY: 'auto',
            overflowX: 'hidden',
            padding: '24px',
            paddingBottom: '280px',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* Initial State */}
          {!isPlaying && (
            <div style={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <div style={{ textAlign: 'center' }}>
                <h3 style={{
                  fontSize: '18px',
                  fontWeight: '600',
                  color: '#ffffff',
                  marginBottom: '8px',
                  textShadow: '0 2px 8px rgba(0, 0, 0, 0.2)'
                }}>
                  Tap play to start live station
                </h3>
                <p style={{
                  fontSize: '12px',
                  color: 'rgba(255, 255, 255, 0.7)',
                  lineHeight: '1.5'
                }}>
                  24/7 coverage of {selectedSources.map(s => s.toUpperCase()).join(', ')}
                </p>
              </div>
            </div>
          )}

 {/* Paused State - Click Play to Resume */}
{isPaused && isPlaying && (
  <div style={{
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    animation: 'fadeIn 0.6s ease'
  }}>
    <div style={{ textAlign: 'center' }}>
      <div style={{
        width: '80px',
        height: '80px',
        borderRadius: '50%',
        background: 'rgba(246, 90, 59, 0.2)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        margin: '0 auto 20px',
      }}>
        <Pause size={40} color="#f65a3b" />
      </div>
      <h3 style={{
        fontSize: '18px',
        fontWeight: '600',
        color: '#ffffff',
        marginBottom: '8px',
        textShadow: '0 2px 8px rgba(0, 0, 0, 0.4)'
      }}>
        Paused
      </h3>
      <p style={{
        fontSize: '14px',
        color: 'rgba(255, 255, 255, 0.7)',
        lineHeight: '1.5'
      }}>
        Click play to resume
      </p>
    </div>
  </div>
)}

{/* Transcript - ONLY show when playing and NOT paused */}
{transcript.length > 0 && !isPaused && isPlaying && (
  <div style={{ marginBottom: '32px' }}>
    {transcript.slice(-1).map((item) => {
      const isSearching = item.text.includes('Searching') || 
                         item.text.includes('One sec') || 
                         item.text.includes('Gathering');
      const isShimmer = item.text === 'shimmer';
      
      // ✅ SHOW SHIMMER LOADING
      if (isShimmer) {
        return <ShimmerLine key={item.timestamp} />;
      }
      
      return (
        <div key={item.timestamp} style={{
          marginBottom: '20px',
          textAlign: 'left',
          animation: 'fadeInUp 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
          display: 'flex',
          alignItems: 'flex-start',
          gap: '12px'
        }}>
          <div style={{
            width: '8px',
            height: '8px',
            borderRadius: '50%',
            background: '#ffffff',
            marginTop: '8px',
            flexShrink: 0,
            animation: 'pulseGlow 1.5s infinite'
          }} />
          <div style={{
            fontSize: '18px',
            fontWeight: '500',
            color: '#ffffff',
            lineHeight: '1.6',
            flex: 1,
            textAlign: 'left',
            ...(isSearching && {
              background: 'linear-gradient(90deg, rgba(255,255,255,0.4) 0%, rgba(255,255,255,0.6) 25%, rgba(255,255,255,1) 50%, rgba(255,255,255,0.6) 75%, rgba(255,255,255,0.4) 100%)',
              backgroundSize: '200% 100%',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              animation: 'textShimmer 3s linear infinite'
            })
          }}>
            {item.text}
          </div>
        </div>
      );
    })}
  </div>
)}
        </div>

        {/* News Cards */}
        {isPlaying && isSearchingComplete && (newsCards.length > 0 || isLoadingCards) && (
          <div style={{
            position: 'fixed',
            bottom: '110px',
            left: 0,
            right: 0,
            zIndex: 25,
            pointerEvents: 'none',
            paddingTop: '20px'
          }}>
            <div 
              ref={newsCardsRef}
              style={{
                display: 'flex',
                gap: '12px',
                overflowX: 'auto',
                paddingLeft: '24px',
                paddingRight: '24px',
                paddingTop: '15px',
                paddingBottom: '34px',
                scrollbarWidth: 'none',
                msOverflowStyle: 'none',
                scrollBehavior: 'smooth',
                pointerEvents: 'auto'
              }}
              className="news-cards-scroll"
            >
              {isLoadingCards ? (
                [...Array(5)].map((_, idx) => (
                  <div
                    key={`skeleton-${idx}`}
                    style={{
                      minWidth: '130px',
                      maxWidth: '130px',
                      height: '160px',
                      borderRadius: '12px',
                      background: 'rgba(255, 255, 255, 0.08)',
                      backdropFilter: 'blur(20px)',
                      border: '1px solid rgba(255, 255, 255, 0.15)',
                      overflow: 'hidden',
                      position: 'relative',
                      flexShrink: 0,
                      animation: 'fadeInScale 0.4s ease forwards',
                      animationDelay: `${idx * 0.08}s`,
                      opacity: 0
                    }}
                  >
                    <div style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.12), transparent)',
                      animation: 'shimmer 2s infinite',
                      zIndex: 1
                    }} />
                  </div>
                ))
              ) : (
                <>
                  {newsCards.map((card, idx) => {
                    const isCurrentlyPlaying = card.isActive;
                    const isPastCard = card.isReady && !card.isActive;
                    
                    return (
                      <div
                        key={card.id}
                        style={{
                          minWidth: '130px',
                          maxWidth: '130px',
                          height: '160px',
                          borderRadius: '12px',
                          background: isCurrentlyPlaying 
                            ? 'rgba(255, 255, 255, 0.18)'
                            : 'rgba(255, 255, 255, 0.08)',
                          backdropFilter: 'blur(20px)',
                          border: isCurrentlyPlaying 
                            ? '2px solid rgba(255, 255, 255, 0.95)' 
                            : '1px solid rgba(255, 255, 255, 0.15)',
                          overflow: 'hidden',
                          transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                          opacity: isCurrentlyPlaying ? 1 : (isPastCard ? 0.4 : 0.25),
                          transform: isCurrentlyPlaying ? 'scale(1.08)' : 'scale(1)',
                          boxShadow: isCurrentlyPlaying 
                            ? '0 12px 32px rgba(0, 0, 0, 0.4), 0 0 0 4px rgba(255, 255, 255, 0.25)' 
                            : '0 4px 16px rgba(0, 0, 0, 0.2)',
                          position: 'relative',
                          cursor: 'pointer',
                          flexShrink: 0,
                          filter: isCurrentlyPlaying ? 'brightness(1.15) saturate(1.1)' : 'brightness(0.9)'
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleArticleClick(card);
                        }}
                      >
                        <div style={{
                          width: '100%',
                          height: '90px',
                          overflow: 'hidden',
                          position: 'relative',
                          borderRadius: '12px 12px 0 0'
                        }}>
                          <img 
                            src={card.image}
                            alt={card.title}
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                              transition: 'transform 0.4s ease',
                              transform: isCurrentlyPlaying ? 'scale(1.05)' : 'scale(1)'
                            }}
                          />
                          
                          {isCurrentlyPlaying && (
                            <div style={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              right: 0,
                              bottom: 0,
                              background: 'linear-gradient(to top, rgba(0,0,0,0.7), transparent 60%)',
                              display: 'flex',
                              alignItems: 'flex-end',
                              justifyContent: 'center',
                              padding: '8px'
                            }}>
                              <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px',
                                color: '#fff',
                                fontSize: '9px',
                                fontWeight: '700',
                                textTransform: 'uppercase',
                                letterSpacing: '0.6px',
                                animation: 'pulseText 1.5s infinite'
                              }}>
                                <Radio size={10} strokeWidth={2.5} />
                                <span>Now Playing</span>
                              </div>
                            </div>
                          )}
                        </div>
                        
                        <div style={{
                          padding: '10px',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '4px'
                        }}>
                          <div style={{
                            fontSize: '8px',
                            fontWeight: '700',
                            color: isCurrentlyPlaying ? '#93c5fd' : 'rgba(147, 197, 253, 0.5)',
                            textTransform: 'uppercase',
                            letterSpacing: '0.6px',
                            textAlign: 'left'
                          }}>
                            {card.source}
                          </div>
                          
                          <div style={{
                            fontSize: '11px',
                            fontWeight: '600',
                            color: isCurrentlyPlaying ? '#fff' : 'rgba(255, 255, 255, 0.7)',
                            lineHeight: '1.3',
                            textAlign: 'left',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            WebkitLineClamp: 3,
                            WebkitBoxOrient: 'vertical'
                          }}>
                            {card.title}
                          </div>
                        </div>
                        
                        {isCurrentlyPlaying && (
                          <div style={{
                            position: 'absolute',
                            top: -3,
                            left: -3,
                            right: -3,
                            bottom: -3,
                            borderRadius: '14px',
                            background: 'linear-gradient(135deg, rgba(255,255,255,0.6), rgba(147,197,253,0.4), rgba(255,255,255,0.6))',
                            backgroundSize: '200% 200%',
                            animation: 'gradientShift 3s ease infinite',
                            zIndex: -1
                          }} />
                        )}
                      </div>
                    );
                  })}
                  
                  {/* Refresh Card */}
                  <div
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRefreshClick();
                    }}
                    style={{
                      minWidth: '130px',
                      maxWidth: '130px',
                      height: '160px',
                      borderRadius: '12px',
                      background: 'rgba(255, 255, 255, 0.12)',
                      backdropFilter: 'blur(20px)',
                      border: '2px dashed rgba(255, 255, 255, 0.4)',
                      overflow: 'hidden',
                      transition: 'all 0.3s ease',
                      cursor: 'pointer',
                      flexShrink: 0,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = 'rgba(255, 255, 255, 0.18)';
                      e.currentTarget.style.transform = 'scale(1.05)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'rgba(255, 255, 255, 0.12)';
                      e.currentTarget.style.transform = 'scale(1)';
                    }}
                  >
                    <div style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '50%',
                      background: 'rgba(255, 255, 255, 0.2)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <RefreshCw size={20} color="#fff" />
                    </div>
                    
                    <div style={{
                      fontSize: '11px',
                      fontWeight: '600',
                      color: '#fff',
                      textAlign: 'center',
                      padding: '0 10px'
                    }}>
                      Search Fresh News
                    </div>
                    
                    <div style={{
                      fontSize: '8px',
                      fontWeight: '500',
                      color: 'rgba(255, 255, 255, 0.6)',
                      textAlign: 'center'
                    }}>
                      Tap to refresh
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* Controls */}
<div style={{
  background: 'rgba(244, 251, 255, 0.15)',
  backdropFilter: 'blur(10px)',
  padding: '16px 20px 24px',
  borderTop: '1px solid rgba(255, 255, 255, 0.56)',
  borderRadius: '42px 42px 0 0',
  position: 'relative',
  zIndex: 30
}}>

  
  <div style={{
    background: '#ffffff',
    borderRadius: '32px',
    padding: '8px 20px',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
    border: '1px solid rgba(169, 177, 185, 0.3)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '16px'
  }}>
    <button
      onClick={changeSpeed}
      style={{
        padding: '12px 16px',
        borderRadius: '20px',
        background: '#f3f4f6',
        border: 'none',
        cursor: 'pointer',
        fontSize: '14px',
        fontWeight: '600',
        color: '#000',
        transition: 'all 0.2s'
      }}
      onMouseEnter={(e) => e.currentTarget.style.background = '#e5e7eb'}
      onMouseLeave={(e) => e.currentTarget.style.background = '#f3f4f6'}
    >
      {playbackSpeed}x
    </button>

<button
  onClick={handlePrevCard}
  disabled={currentCardIndex === 0 || !isPlaying}
  style={{
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    background: currentCardIndex === 0 || !isPlaying ? '#e5e7eb' : '#f3f4f6',
    border: 'none',
    cursor: currentCardIndex === 0 || !isPlaying ? 'not-allowed' : 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    opacity: currentCardIndex === 0 || !isPlaying ? 0.4 : 1,
    transition: 'all 0.2s'
  }}
  onMouseEnter={(e) => {
    if (currentCardIndex > 0 && isPlaying) {
      e.currentTarget.style.background = '#e5e7eb';
    }
  }}
  onMouseLeave={(e) => {
    if (currentCardIndex > 0 && isPlaying) {
      e.currentTarget.style.background = '#f3f4f6';
    }
  }}
>
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
    <path 
      d="M11 19l-7-7 7-7m8 14l-7-7 7-7" 
      stroke="#000" 
      strokeWidth="2" 
      strokeLinecap="round" 
      strokeLinejoin="round"
    />
  </svg>
</button>

    <button
      onClick={togglePlayPause}
      disabled={isGenerating}
      style={{
        width: '58px',
        height: '58px',
        borderRadius: '50%',
        background: isGenerating ? '#6b7280' : '#000',
        border: 'none',
        cursor: isGenerating ? 'not-allowed' : 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'all 0.2s',
        opacity: isGenerating ? 0.6 : 1
      }}
      onMouseEnter={(e) => {
        if (!isGenerating) {
          e.currentTarget.style.transform = 'scale(1.05)';
        }
      }}
      onMouseLeave={(e) => {
        if (!isGenerating) {
          e.currentTarget.style.transform = 'scale(1)';
        }
      }}
    >
      {isPaused || !isPlaying ? (
        <Play size={24} color="white" fill="white" />
      ) : (
        <Pause size={24} color="white" fill="white" />
      )}
    </button>

<button
  onClick={handleNextCard}
  disabled={currentCardIndex >= newsCards.length - 1 || !isPlaying}
  style={{
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    background: currentCardIndex >= newsCards.length - 1 || !isPlaying ? '#e5e7eb' : '#f3f4f6',
    border: 'none',
    cursor: currentCardIndex >= newsCards.length - 1 || !isPlaying ? 'not-allowed' : 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    opacity: currentCardIndex >= newsCards.length - 1 || !isPlaying ? 0.4 : 1,
    transition: 'all 0.2s'
  }}
  onMouseEnter={(e) => {
    if (currentCardIndex < newsCards.length - 1 && isPlaying) {
      e.currentTarget.style.background = '#e5e7eb';
    }
  }}
  onMouseLeave={(e) => {
    if (currentCardIndex < newsCards.length - 1 && isPlaying) {
      e.currentTarget.style.background = '#f3f4f6';
    }
  }}
>
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
    <path 
      d="M13 5l7 7-7 7M4 5l7 7-7 7" 
      stroke="#000" 
      strokeWidth="2" 
      strokeLinecap="round" 
      strokeLinejoin="round"
    />
  </svg>
</button>
  </div>
</div>
      </div>

      {/* Article Confirmation Modal */}
{/* Article Confirmation Modal */}
{showArticleConfirm && selectedArticle && (
  <div style={{
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0, 0, 0, 0.8)',
    backdropFilter: 'blur(10px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 40,
    animation: 'fadeIn 0.3s ease'
  }}>
    <div style={{
      background: 'rgba(255, 255, 255, 0.1)',
      borderRadius: '24px',
      padding: '32px',
      maxWidth: '400px',
      margin: '0 20px',
      border: '1px solid rgba(255, 255, 255, 0.2)',
      animation: 'scaleIn 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
      textAlign: 'center'
    }}>
      {/* ✅ ADD IMAGE HERE */}
      <div style={{
        width: '100%',
        height: '120px',
        borderRadius: '12px',
        overflow: 'hidden',
        marginBottom: '20px'
      }}>
        <img 
          src={selectedArticle.image}
          alt={selectedArticle.fullTitle}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover'
          }}
          onError={(e) => {
            // Fallback image if load fails
            e.target.src = '/image/studycard7.png';
          }}
        />
      </div>
      
      <h3 style={{
        fontSize: '18px',
        fontWeight: '600',
        color: '#ffffff',
        marginBottom: '8px',
        lineHeight: '1.4'
      }}>
        {selectedArticle.fullTitle}
      </h3>
      
      {/* ✅ ADD SOURCE */}
      <p style={{
        fontSize: '12px',
        color: 'rgba(255, 255, 255, 0.5)',
        marginBottom: '4px'
      }}>
        {selectedArticle.source}
      </p>
      
      <p style={{
        fontSize: '14px',
        color: 'rgba(255, 255, 255, 0.7)',
        marginBottom: '24px',
        lineHeight: '1.5'
      }}>
        Would you like to read the full article?
      </p>
      
      <div style={{
        display: 'flex',
        gap: '12px',
        justifyContent: 'center'
      }}>
        <button
          onClick={cancelArticle}
          style={{
            padding: '12px 24px',
            borderRadius: '20px',
            background: 'rgba(255, 255, 255, 0.1)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            color: '#ffffff',
            fontSize: '14px',
            fontWeight: '600',
            cursor: 'pointer',
            transition: 'all 0.2s'
          }}
          onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.15)'}
          onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)'}
        >
          Cancel
        </button>
        
        <button
          onClick={confirmArticle}
          style={{
            padding: '12px 24px',
            borderRadius: '20px',
            background: '#ffffff',
            border: 'none',
            color: '#000000',
            fontSize: '14px',
            fontWeight: '600',
            cursor: 'pointer',
            transition: 'all 0.2s'
          }}
          onMouseEnter={(e) => e.currentTarget.style.opacity = '0.9'}
          onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
        >
          Read Article
        </button>
      </div>
    </div>
  </div>
)}
      {/* Refresh Confirmation Modal */}
      {showRefreshConfirm && (
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.8)',
          backdropFilter: 'blur(10px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 40,
          animation: 'fadeIn 0.3s ease'
        }}>
          <div style={{
            background: 'rgba(255, 255, 255, 0.1)',
            borderRadius: '24px',
            padding: '32px',
            maxWidth: '400px',
            margin: '0 20px',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            animation: 'scaleIn 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
            textAlign: 'center'
          }}>
            <div style={{
              width: '60px',
              height: '60px',
              borderRadius: '50%',
              background: 'rgba(255, 255, 255, 0.15)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 20px'
            }}>
              <RefreshCw size={28} color="#fff" />
            </div>
            
            <h3 style={{
              fontSize: '18px',
              fontWeight: '600',
              color: '#ffffff',
              marginBottom: '8px'
            }}>
              Search for Fresh News?
            </h3>
            
            <p style={{
              fontSize: '14px',
              color: 'rgba(255, 255, 255, 0.7)',
              marginBottom: '24px'
            }}>
              We'll search for the latest stories and start a new cycle.
            </p>
            
            <div style={{
              display: 'flex',
              gap: '12px',
              justifyContent: 'center'
            }}>
              <button
                onClick={cancelRefresh}
                style={{
                  padding: '12px 24px',
                  borderRadius: '20px',
                  background: 'rgba(255, 255, 255, 0.1)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  color: '#ffffff',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              
              <button
                onClick={confirmRefresh}
                style={{
                  padding: '12px 24px',
                  borderRadius: '20px',
                  background: '#ffffff',
                  border: 'none',
                  color: '#000000',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        
        @keyframes scaleIn {
          from {
            opacity: 0;
            transform: scale(0.9);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
        
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes pulseGlow {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.5; transform: scale(1.2); }
        }
        
        @keyframes textShimmer {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
        
        @keyframes fadeInScale {
          0% {
            opacity: 0;
            transform: scale(0.9);
          }
          100% {
            opacity: 1;
            transform: scale(1);
          }
        }

        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }

        @keyframes pulseText {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.8; }
        }

        @keyframes gradientShift {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        
        .news-cards-scroll::-webkit-scrollbar {
          display: none;
        }
        
        .news-cards-scroll {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>
    </div>
  );
};

export default SelectedNewsStation;